/**
 * DNACore V4 — Editor de Avaliações Premium (TipTap)
 * Rota: /v2/academico/editor-avaliacoes/:id e /equipe/avaliacoes/editor/:id
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import { DOMSerializer } from '@tiptap/pm/model';
import StarterKit from '@tiptap/starter-kit';
import { ResizableImage } from './ResizableImage';
import { VariableBox } from './VariableBox';
import { ResizableTextBox } from './ResizableTextBox';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
import Highlight from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline as UIcon, Strikethrough, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Heading1, Heading2, Heading3, Image as ImgIcon, Table as TableIcon, Minus,
  Quote, Save, Printer, FileDown, Copy as CopyIcon, ArrowLeft, Highlighter, Link2, Undo, Redo, Plus,
  Sparkles, X, Loader2, Wand2, ListPlus, Maximize2, Minimize2, Languages, ChevronDown, ChevronUp,
  History, RotateCcw, BookmarkPlus, Brain, Send, MessageSquare, ImagePlus,
  ClipboardCheck, Library, Star, Trash2, Search, FileCheck2, MoreHorizontal, Type,
} from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';
import './AssessmentEditor.css';

const STATUS_LABELS = {
  draft: 'Rascunho',
  review: 'Em revisão',
  approved: 'Aprovada',
  archived: 'Arquivada',
};

// V4.14: labels derivados de print_queue_status (visíveis ao lado do "Rascunho")
const PRINT_STATUS = {
  rascunho: { label: 'Rascunho', bg: '#f1f5f9', fg: '#475569', bd: '#e2e8f0' },
  enviada:  { label: 'Enviada para Impressão', bg: '#dbeafe', fg: '#1e40af', bd: '#93c5fd' },
  impressa: { label: 'Impressa', bg: '#dcfce7', fg: '#15803d', bd: '#86efac' },
};

// ─── AI History helpers (localStorage) ───
const AI_HIST_KEY = (id) => `dnacore_ai_hist_${id || 'novo'}`;
const AI_WEEK_KEY = 'dnacore_ai_weekly';
const AI_HIST_LIMIT = 20;
const AI_ACTION_LABEL = {
  generate: 'Geração com IA',
  refine: 'Refinar',
  distractors: 'Alternativas',
  expand: 'Expandir',
  summarize: 'Resumir',
  translate: 'Traduzir',
  easier: 'Mais fácil',
  harder: 'Mais difícil',
};

const _isoWeek = (d = new Date()) => {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const wk = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(wk).padStart(2, '0')}`;
};

const readAIHistory = (id) => {
  try { return JSON.parse(localStorage.getItem(AI_HIST_KEY(id)) || '[]'); } catch { return []; }
};

const pushAIHistory = (id, entry) => {
  const list = readAIHistory(id);
  list.unshift(entry);
  if (list.length > AI_HIST_LIMIT) list.length = AI_HIST_LIMIT;
  try { localStorage.setItem(AI_HIST_KEY(id), JSON.stringify(list)); } catch { /* quota */ }
  // weekly counter
  try {
    const wk = _isoWeek();
    const cur = JSON.parse(localStorage.getItem(AI_WEEK_KEY) || '{}');
    const next = (cur.week === wk) ? { week: wk, count: (cur.count || 0) + 1 } : { week: wk, count: 1 };
    localStorage.setItem(AI_WEEK_KEY, JSON.stringify(next));
  } catch { /* noop */ }
  return list;
};

const readWeeklyCount = () => {
  try {
    const cur = JSON.parse(localStorage.getItem(AI_WEEK_KEY) || '{}');
    if (cur.week === _isoWeek()) return cur.count || 0;
  } catch { /* noop */ }
  return 0;
};


const TYPE_LABELS = {
  trimestral: 'Avaliação Trimestral',
  apa: 'APA',
  trabalho: 'Trabalho',
  simulado: 'Simulado',
  recuperacao: 'Recuperação',
  diagnostica: 'Diagnóstica',
  chamada_oral: 'Chamada Oral',
  atividades: 'Folha de Atividades',
};

export default function AssessmentEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState({
    title: 'Nova Avaliação',
    type: 'trimestral',
    subject_name: '',
    class_name: '',
    teacher_name: '',
    trimester: '',
    year: new Date().getFullYear(),
    valor: '',
    status: 'draft',
    // V4.5: campos do cabeçalho editável
    aluno_nome: '',
    numero_chamada: '',
    type_label: '',
    data_label: '',
    // V4.6: Header Template Picker
    header_template: 'dna_2026',
    // V4.11: status da fila de impressão
    print_queue_status: 'rascunho',
    print_queue_id: null,
    // V4.17: overrides de posicionamento dos slots do cabeçalho (por slot)
    // { nome: { detached: true, x: 200, y: 50, w: 300, h: 40 }, ... }
    header_slot_overrides: {},
  });

  // V4.7: Custom Templates Manager
  const [tplMgrOpen, setTplMgrOpen] = useState(false);
  const [tplList, setTplList] = useState([]);
  const [tplBusy, setTplBusy] = useState(false);
  const [tplForm, setTplForm] = useState({ label: '', description: '', file: null });

  const loadTemplatesList = useCallback(async () => {
    setTplBusy(true);
    try {
      const { data } = await api.get('/academic/assessments/header-templates');
      setTplList(data?.templates || []);
    } catch (e) { toast.error('Falha: ' + e.message); }
    finally { setTplBusy(false); }
  }, []);

  // V4.7: load templates list on mount
  useEffect(() => { loadTemplatesList(); }, [loadTemplatesList]);

  // Helper: URL do background do template atual (built-in OU custom)
  const getCurrentTemplateBg = useCallback(() => {
    const key = meta.header_template || 'dna_2026';
    const tpl = tplList.find((t) => t.key === key);
    if (!tpl || !tpl.has_bg) return null;
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    if (tpl.is_custom) return `${base}/api/academic/assessments/header-templates/${key}/bg.png`;
    if (key === 'dna_2026') return `${base}/api/academic/assessments/static/header_bg.png`;
    return null;
  }, [meta.header_template, tplList]);

  const uploadCustomTemplate = async () => {
    if (!tplForm.file || !tplForm.label.trim()) {
      toast.error('Selecione um PNG e dê um nome.');
      return;
    }
    setTplBusy(true);
    try {
      const fd = new FormData();
      fd.append('label', tplForm.label.trim());
      fd.append('description', tplForm.description.trim());
      fd.append('positions_json', '{}');
      fd.append('file', tplForm.file);
      const { data } = await api.post('/academic/assessments/header-templates/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Template criado: ' + data.key);
      setTplForm({ label: '', description: '', file: null });
      await loadTemplatesList();
      // V4.8: auto-abre o Position Editor para calibrar as caixas sobre o novo PNG
      if (data?.template) {
        setTplMgrOpen(false);
        setTimeout(() => openPosEditor({ ...data.template, key: data.key, is_custom: true }), 200);
      }
    } catch (e) { toast.error('Falha: ' + (e.response?.data?.detail || e.message)); }
    finally { setTplBusy(false); }
  };

  const deleteCustomTemplate = async (key) => {
    if (!window.confirm('Remover este template?')) return;
    try {
      await api.delete(`/academic/assessments/header-templates/${key}`);
      toast.success('Removido');
      if (meta.header_template === key) setMeta({ ...meta, header_template: 'dna_2026' });
      loadTemplatesList();
    } catch (e) { toast.error('Falha: ' + (e.response?.data?.detail || e.message)); }
  };

  // V4.9: Variable Boxes — auto-fill via window event
  useEffect(() => {
    const VAR_MAP = () => ({
      aluno_nome: meta.aluno_nome || '',
      numero_chamada: meta.numero_chamada || '',
      turma: meta.class_name || '',
      disciplina: meta.subject_name || '',
      professor: meta.teacher_name || '',
      trimestre: meta.trimester ? `${meta.trimester}º` : '',
      data: meta.data_label || '',
      valor: meta.valor !== '' && meta.valor !== null && meta.valor !== undefined
        ? Number(meta.valor).toFixed(2).replace('.', ',') : '',
      nota: '',
    });
    const broadcast = () => {
      window.dispatchEvent(new CustomEvent('ae-var-resolve', { detail: VAR_MAP() }));
    };
    const onRequest = () => broadcast();
    window.addEventListener('ae-var-request', onRequest);
    broadcast();
    return () => window.removeEventListener('ae-var-request', onRequest);
  }, [meta]);

  const [varMenuOpen, setVarMenuOpen] = useState(false);

  // V4.17: Instrumenta cada slot do cabeçalho (data-slot) com botão pin/unpin + drag + resize.
  // Quando "desancorado", a caixa vira móvel livre (position:absolute com x/y/w/h em pixels).
  // Persiste em meta.header_slot_overrides.
  const metaRef = useRef(meta);
  useEffect(() => { metaRef.current = meta; }, [meta]);

  useEffect(() => {
    // Aguarda o header aparecer no DOM (pode não existir ainda no primeiro tick)
    const header = document.querySelector('[data-testid="ae-hdr-editable"]');
    if (!header) {
      const t = setTimeout(() => setMeta((m) => ({ ...m })), 120); // reagenda ao próximo render
      return () => clearTimeout(t);
    }
    if ((meta.header_template || 'dna_2026') !== 'dna_2026') return; // só no template DNA 2026

    const slots = header.querySelectorAll('.ae-hdr-field[data-slot]');
    const cleanups = [];

    const persistOverride = (slot, patch) => {
      const cur = metaRef.current;
      const prev = cur.header_slot_overrides || {};
      const next = { ...prev, [slot]: { ...(prev[slot] || {}), ...patch } };
      setMeta({ ...cur, header_slot_overrides: next });
    };
    const clearOverride = (slot) => {
      const cur = metaRef.current;
      const prev = cur.header_slot_overrides || {};
      const { [slot]: _drop, ...rest } = prev;
      void _drop;
      setMeta({ ...cur, header_slot_overrides: rest });
    };

    const applyStyle = (el, slot) => {
      const ov = (metaRef.current.header_slot_overrides || {})[slot];
      if (ov && ov.detached) {
        el.style.position = 'absolute';
        el.style.left = `${Math.round(ov.x || 0)}px`;
        el.style.top = `${Math.round(ov.y || 0)}px`;
        el.style.width = `${Math.round(ov.w || 120)}px`;
        el.style.height = `${Math.round(ov.h || 24)}px`;
        el.style.zIndex = '20';
        el.style.background = 'rgba(255,255,255,0.85)';
        el.style.border = '1px dashed #7B3F98';
        el.style.borderRadius = '4px';
        el.classList.add('is-detached');
      } else {
        el.style.position = '';
        el.style.left = '';
        el.style.top = '';
        el.style.width = '';
        el.style.height = '';
        el.style.zIndex = '';
        el.style.background = '';
        el.style.border = '';
        el.style.borderRadius = '';
        el.classList.remove('is-detached');
      }
    };

    slots.forEach((el) => {
      const slot = el.getAttribute('data-slot');
      applyStyle(el, slot);

      // botão pin/unpin (topo direita) — só aparece no hover via CSS
      let pinBtn = el.querySelector(':scope > .ae-hdr-slot-pin');
      if (!pinBtn) {
        pinBtn = document.createElement('button');
        pinBtn.type = 'button';
        pinBtn.className = 'ae-hdr-slot-pin no-print';
        pinBtn.title = 'Desancorar/Ancorar campo';
        pinBtn.setAttribute('data-testid', `slot-pin-${slot}`);
        pinBtn.innerHTML = '⇱';
        pinBtn.style.cssText = 'position:absolute;top:-10px;right:-6px;width:18px;height:18px;border:0;border-radius:50%;background:#7B3F98;color:#fff;font-size:11px;font-weight:700;cursor:pointer;display:none;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(15,23,42,0.25);z-index:30;';
        el.appendChild(pinBtn);
      }
      // handle de resize (só quando desancorado)
      let resizeH = el.querySelector(':scope > .ae-hdr-slot-resize');
      if (!resizeH) {
        resizeH = document.createElement('span');
        resizeH.className = 'ae-hdr-slot-resize no-print';
        resizeH.setAttribute('data-testid', `slot-resize-${slot}`);
        resizeH.style.cssText = 'position:absolute;right:-5px;bottom:-5px;width:10px;height:10px;background:#7B3F98;border:2px solid #fff;border-radius:2px;cursor:nwse-resize;display:none;z-index:30;';
        el.appendChild(resizeH);
      }
      // V4.17.1: barra de arrasto dedicada (topo, só visível quando desancorado)
      let dragH = el.querySelector(':scope > .ae-hdr-slot-drag');
      if (!dragH) {
        dragH = document.createElement('span');
        dragH.className = 'ae-hdr-slot-drag no-print';
        dragH.setAttribute('data-testid', `slot-drag-${slot}`);
        dragH.title = 'Arrastar campo';
        dragH.innerHTML = '⠿';
        dragH.style.cssText = 'position:absolute;left:0;right:22px;top:-14px;height:12px;background:#7B3F98;color:#fff;font-size:10px;line-height:12px;text-align:center;font-weight:700;cursor:grab;display:none;border-top-left-radius:4px;border-top-right-radius:4px;user-select:none;z-index:29;letter-spacing:2px;';
        el.appendChild(dragH);
      }

      const showChrome = () => {
        pinBtn.style.display = 'inline-flex';
        const ov = (metaRef.current.header_slot_overrides || {})[slot];
        const isDetached = !!(ov && ov.detached);
        resizeH.style.display = isDetached ? 'inline-block' : 'none';
        dragH.style.display   = isDetached ? 'block' : 'none';
        pinBtn.innerHTML = isDetached ? '⇲' : '⇱';
        pinBtn.title = isDetached ? 'Reancorar (voltar ao slot)' : 'Desancorar (mover livremente)';
      };
      const hideChrome = () => {
        pinBtn.style.display = 'none';
        resizeH.style.display = 'none';
        // mantém a barra de arrasto visível enquanto o slot estiver desancorado
        const ov = (metaRef.current.header_slot_overrides || {})[slot];
        dragH.style.display = (ov && ov.detached) ? 'block' : 'none';
      };
      el.addEventListener('mouseenter', showChrome);
      el.addEventListener('mouseleave', hideChrome);
      // aplica visibilidade inicial (para slots já desancorados na carga)
      hideChrome();

      const onPin = (ev) => {
        ev.preventDefault(); ev.stopPropagation();
        const ov = (metaRef.current.header_slot_overrides || {})[slot];
        if (ov && ov.detached) {
          clearOverride(slot);
        } else {
          const r = el.getBoundingClientRect();
          const p = el.parentElement.getBoundingClientRect();
          persistOverride(slot, { detached: true, x: r.left - p.left, y: r.top - p.top, w: r.width, h: r.height });
        }
      };
      pinBtn.addEventListener('click', onPin);

      // V4.17.1: drag pela barra dedicada (handle) — texto continua editável normalmente
      const startDrag = (ev) => {
        const ov = (metaRef.current.header_slot_overrides || {})[slot];
        if (!ov || !ov.detached) return;
        ev.preventDefault(); ev.stopPropagation();
        const startX = ev.clientX; const startY = ev.clientY;
        const origX = ov.x || 0; const origY = ov.y || 0;
        dragH.style.cursor = 'grabbing';
        const onMove = (e) => {
          persistOverride(slot, { x: origX + (e.clientX - startX), y: origY + (e.clientY - startY) });
        };
        const onUp = () => {
          dragH.style.cursor = 'grab';
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
      };
      dragH.addEventListener('mousedown', startDrag);

      const onResizeDown = (ev) => {
        ev.preventDefault(); ev.stopPropagation();
        const ov = (metaRef.current.header_slot_overrides || {})[slot];
        if (!ov || !ov.detached) return;
        const startX = ev.clientX; const startY = ev.clientY;
        const origW = ov.w || el.offsetWidth; const origH = ov.h || el.offsetHeight;
        const onMove = (e) => {
          persistOverride(slot, { w: Math.max(60, origW + (e.clientX - startX)), h: Math.max(20, origH + (e.clientY - startY)) });
        };
        const onUp = () => {
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
      };
      resizeH.addEventListener('mousedown', onResizeDown);

      cleanups.push(() => {
        el.removeEventListener('mouseenter', showChrome);
        el.removeEventListener('mouseleave', hideChrome);
        dragH.removeEventListener('mousedown', startDrag);
        pinBtn.removeEventListener('click', onPin);
        resizeH.removeEventListener('mousedown', onResizeDown);
      });
    });

    return () => { cleanups.forEach((fn) => fn()); };
  }, [meta.header_slot_overrides, meta.header_template]);

  const VARS_LIST = [
    { k: 'aluno_nome',     label: 'Nome do aluno' },
    { k: 'numero_chamada', label: 'Nº de chamada' },
    { k: 'turma',          label: 'Turma' },
    { k: 'disciplina',     label: 'Disciplina' },
    { k: 'professor',      label: 'Professor(a)' },
    { k: 'trimestre',      label: 'Trimestre' },
    { k: 'data',           label: 'Data' },
    { k: 'valor',          label: 'Valor' },
    { k: 'nota',           label: 'Nota' },
  ];
  const insertVariable = (varName) => {
    if (!editor) return;
    editor.chain().focus().insertContent({
      type: 'variableBox',
      attrs: { varName, floating: false, width: 140 },
    }).run();
    setVarMenuOpen(false);
    setTimeout(() => save(true), 400);
  };

  // V4.11: Enviar para Secretaria (fila de impressão)
  const [secretaryOpen, setSecretaryOpen] = useState(false);
  const [secretaryBusy, setSecretaryBusy] = useState(false);
  const [secretaryForm, setSecretaryForm] = useState({ copias_extras: 0, data_prova: '', plano: '' });

  const sendToSecretary = () => {
    if (meta.print_queue_status === 'enviada' || meta.print_queue_status === 'impressa') {
      toast.message('Esta avaliação já está na fila de impressão.');
      return;
    }
    // garante save antes
    setSecretaryForm({ copias_extras: 0, data_prova: meta.data_label || '', plano: '' });
    setSecretaryOpen(true);
  };

  const confirmSendToSecretary = async () => {
    setSecretaryBusy(true);
    try {
      // força save primeiro
      await save(false);
      const { data } = await api.post(`/academic/assessments/${id}/send-to-secretary`, {
        copias_extras: Number(secretaryForm.copias_extras) || 0,
        data_prova: secretaryForm.data_prova || null,
        plano: secretaryForm.plano || null,
      });
      toast.success('Avaliação aprovada e enviada para a secretaria imprimir.');
      setMeta((prev) => ({ ...prev, status: 'approved', print_queue_status: 'enviada', print_queue_id: data.queue_id }));
      setSecretaryOpen(false);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      if (e.response?.status === 409) toast.error('Esta avaliação já está na fila de impressão.');
      else toast.error('Falha: ' + msg);
    } finally { setSecretaryBusy(false); }
  };

  // V4.10: Mala-Direta — gera 1 PDF por aluno da turma num ZIP
  const [mailMergeOpen, setMailMergeOpen] = useState(false);
  const [mailMergeBusy, setMailMergeBusy] = useState(false);
  const [mailMergeClasses, setMailMergeClasses] = useState([]);
  const [mailMergeForm, setMailMergeForm] = useState({ class_group_id: '', mode: 'teacher' });

  const openMailMerge = async () => {
    setMailMergeOpen(true);
    try {
      const { data } = await api.get('/classes');
      setMailMergeClasses(Array.isArray(data) ? data : []);
    } catch (e) { toast.error('Falha ao carregar turmas: ' + e.message); }
  };

  const runMailMerge = async () => {
    if (!mailMergeForm.class_group_id) { toast.error('Selecione uma turma.'); return; }
    setMailMergeBusy(true);
    try {
      const resp = await api.post(
        `/academic/assessments/${id}/export/batch-zip?class_group_id=${encodeURIComponent(mailMergeForm.class_group_id)}&mode=${mailMergeForm.mode}`,
        null,
        { responseType: 'blob' }
      );
      const blob = new Blob([resp.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mala_direta_${(meta.title || 'avaliacao').replace(/\s+/g, '_')}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('ZIP baixado: 1 PDF por aluno');
      setMailMergeOpen(false);
    } catch (e) { toast.error('Falha: ' + (e.response?.data?.detail || e.message)); }
    finally { setMailMergeBusy(false); }
  };

  // V4.8: Visual Positions Editor
  const [posEditorOpen, setPosEditorOpen] = useState(false);
  const [posEditorTpl, setPosEditorTpl] = useState(null); // template sendo editado
  const [posData, setPosData] = useState({}); // { aluno: {left, top, width}, ... }
  const posCanvasRef = useRef(null);
  const FIELD_KEYS = [
    { key: 'aluno', label: 'Nome',        color: '#3b82f6' },
    { key: 'num',   label: 'Nº',          color: '#10b981' },
    { key: 'ano',   label: 'Ano',         color: '#f59e0b' },
    { key: 'disc',  label: 'Disciplina',  color: '#ef4444' },
    { key: 'prof',  label: 'Professor',   color: '#8E52AB' },
    { key: 'tipo',  label: 'Tipo',        color: '#ec4899' },
    { key: 'valor', label: 'Valor',       color: '#06b6d4' },
    { key: 'data',  label: 'Data',        color: '#84cc16' },
    { key: 'nota',  label: 'Nota',        color: '#f97316' },
  ];
  const DEFAULT_POS = {
    aluno: { left: 47, top: 13, width: 50 },
    num:   { left: 41, top: 35, width: 9  },
    ano:   { left: 53, top: 35, width: 16 },
    disc:  { left: 73, top: 35, width: 24 },
    prof:  { left: 44, top: 57, width: 53 },
    tipo:  { left: 38, top: 75, width: 60 },
    valor: { left: 41, top: 90, width: 16 },
    data:  { left: 60, top: 90, width: 16 },
    nota:  { left: 79, top: 90, width: 16 },
  };

  const openPosEditor = (tpl) => {
    setPosEditorTpl(tpl);
    setPosData({ ...DEFAULT_POS, ...(tpl.positions || {}) });
    setPosEditorOpen(true);
  };

  const savePositions = async () => {
    if (!posEditorTpl) return;
    try {
      await api.patch(`/academic/assessments/header-templates/${posEditorTpl.key}/positions`, { positions: posData });
      toast.success('Posições salvas');
      setPosEditorOpen(false);
      loadTemplatesList();
    } catch (e) { toast.error('Falha: ' + (e.response?.data?.detail || e.message)); }
  };

  // Drag/resize handler para uma caixa no canvas
  const startPosDrag = (e, fieldKey, mode) => {
    e.preventDefault(); e.stopPropagation();
    const canvas = posCanvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const orig = { ...posData[fieldKey] };

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      const dxPct = (dx / rect.width) * 100;
      const dyPct = (dy / rect.height) * 100;
      setPosData((prev) => ({
        ...prev,
        [fieldKey]: mode === 'drag' ? {
          ...orig,
          left: Math.max(0, Math.min(100 - orig.width, orig.left + dxPct)),
          top:  Math.max(0, Math.min(98, orig.top + dyPct)),
        } : {
          ...orig,
          width: Math.max(3, Math.min(100 - orig.left, orig.width + dxPct)),
        },
      }));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  const [savedAt, setSavedAt] = useState(null);
  const [saving, setSaving] = useState(false);
  const initialLoaded = useRef(false);
  const autoSaveTimer = useRef(null);

  // ─── AI Generation ───
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiForm, setAiForm] = useState({
    prompt: '',
    n_objetivas: 10,
    n_dissertativas: 2,
    include_gabarito: true,
    mode: 'append',
  });

  // ─── AI History & weekly counter ───
  const [aiHistory, setAiHistory] = useState([]);
  const [aiHistOpen, setAiHistOpen] = useState(false);
  const [aiWeekly, setAiWeekly] = useState(0);

  useEffect(() => {
    setAiHistory(readAIHistory(id));
    setAiWeekly(readWeeklyCount());
  }, [id]);

  const recordAISnapshot = useCallback((actionKey, beforeHtml, afterHtml) => {
    const entry = {
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      ts: new Date().toISOString(),
      action: actionKey,
      label: AI_ACTION_LABEL[actionKey] || actionKey,
      before: beforeHtml,
      after: afterHtml,
    };
    const list = pushAIHistory(id, entry);
    setAiHistory(list);
    setAiWeekly(readWeeklyCount());
  }, [id]);

  const revertAI = (entryId) => {
    if (!editor) return;
    const entry = aiHistory.find((e) => e.id === entryId);
    if (!entry) return;
    if (!window.confirm(`Reverter "${entry.label}" para o estado anterior?`)) return;
    editor.commands.setContent(entry.before, true);
    toast.success(`Revertido: ${entry.label}`);
    setAiHistOpen(false);
    setTimeout(() => save(true), 400);
  };

  // ─── AI Chat (Assistente IA — Onda 1) ───
  const [chatOpen, setChatOpen] = useState(false);
  const [chatBusy, setChatBusy] = useState(false);
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState([
    {
      role: 'assistant',
      content: 'Olá! Sou seu Assistente IA. Posso criar provas, reescrever questões, ajustar dificuldade, alinhar à BNCC e muito mais. Digite seu pedido em linguagem natural.',
    },
  ]);
  const chatBottomRef = useRef(null);

  useEffect(() => {
    if (chatBottomRef.current) chatBottomRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatBusy]);

  const sendChat = async (overridePrompt) => {
    if (!editor) return;
    const text = (overridePrompt ?? chatInput).trim();
    if (!text) return;
    const newHistory = [...chatMessages, { role: 'user', content: text }];
    setChatMessages(newHistory);
    setChatInput('');
    setChatBusy(true);
    try {
      const { data } = await api.post('/academic/assessments/chat-ai', {
        messages: newHistory.filter((m) => m.role === 'user' || m.role === 'assistant').slice(-12),
        current_html: editor.getHTML().slice(0, 60000),
        subject: meta.subject_name || null,
        grade: meta.class_name || null,
        type: meta.type || null,
      });
      const reply = data?.reply || 'Pronto.';
      const action = data?.action || { type: 'none' };
      setChatMessages([...newHistory, { role: 'assistant', content: reply }]);

      if (action.type === 'replace' && action.html) {
        const before = editor.getHTML();
        editor.commands.setContent(action.html, true);
        recordAISnapshot('generate', before, editor.getHTML());
        toast.success('Prova atualizada pela IA');
        setTimeout(() => save(true), 500);
      } else if (action.type === 'append' && action.html) {
        const before = editor.getHTML();
        editor.commands.focus('end');
        editor.commands.insertContent(action.html);
        recordAISnapshot('generate', before, editor.getHTML());
        toast.success('Conteúdo adicionado pela IA');
        setTimeout(() => save(true), 500);
      }
    } catch (e) {
      const errMsg = e.response?.data?.detail || e.message;
      setChatMessages([...newHistory, { role: 'assistant', content: `Erro: ${errMsg}` }]);
      toast.error('Falha no chat IA');
    } finally {
      setChatBusy(false);
    }
  };

  const CHAT_QUICK_ACTIONS = [
    'Crie uma avaliação completa de Matemática para o 7º Ano com 12 questões',
    'Faça uma prova de História sobre Revolução Francesa',
    'Faça a prova ficar mais difícil',
    'Simplifique todas as questões',
    'Transforme em múltipla escolha',
    'Adicione 5 questões discursivas',
    'Crie uma situação-problema',
    'Misture níveis fácil, médio e difícil',
    'Alinhe à BNCC',
    'Gere uma recuperação',
    'Faça um simulado estilo ENEM',
    'Gere o gabarito comentado',
  ];

  // ─── AI Image Generation (Onda 2) ───
  const [imgOpen, setImgOpen] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const [imgForm, setImgForm] = useState({ prompt: '', size: '1024x1024', style: 'didatico' });

  // V4.3 PREMIUM: lista de estilos didáticos
  const ILLUSTRATION_STYLES = [
    { key: 'didatico',    label: 'Livro Didático',     emoji: '📚' },
    { key: 'realista',    label: 'Realista',           emoji: '📷' },
    { key: 'vetorial',    label: 'Vetorial',           emoji: '🔷' },
    { key: 'infantil',    label: 'Infantil',           emoji: '🧒' },
    { key: 'hq',          label: 'HQ',                 emoji: '💥' },
    { key: 'cientifico',  label: 'Científico',         emoji: '🔬' },
    { key: 'minimalista', label: 'Minimalista',        emoji: '◽' },
    { key: 'aquarela',    label: 'Aquarela',           emoji: '🎨' },
    { key: 'pixar',       label: 'Pixar Educacional',  emoji: '🎬' },
    { key: 'editorial',   label: 'Editorial',          emoji: '📰' },
  ];

  // ─── V4.2: Image Library (galeria reutilizável) ───
  const [libOpen, setLibOpen] = useState(false);
  const [libBusy, setLibBusy] = useState(false);
  const [libItems, setLibItems] = useState([]);
  const [libQ, setLibQ] = useState('');
  const [libScope, setLibScope] = useState('mine'); // V4.3: mine | shared | all
  const [libOnlyFav, setLibOnlyFav] = useState(false); // V4.3 PREMIUM: filtro favoritas

  const loadLibrary = useCallback(async (q = '', scope = 'mine', onlyFav = false) => {
    setLibBusy(true);
    try {
      const params = { scope };
      if (q) params.q = q;
      if (onlyFav) params.favorite = true;
      const { data } = await api.get('/academic/assessments/image-library', { params });
      setLibItems(data?.items || []);
    } catch (e) { toast.error('Falha ao carregar galeria: ' + e.message); }
    finally { setLibBusy(false); }
  }, []);

  const insertFromLibrary = (item) => {
    if (!editor || !item?.url) return;
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
    const fullUrl = item.url.startsWith('http') ? item.url : `${base}${item.url}`;
    editor.chain().focus().setImage({ src: fullUrl, alt: item.prompt?.slice(0, 100) || '', prompt: item.prompt || '' }).run();
    toast.success('Imagem inserida');
    setLibOpen(false);
    setTimeout(() => save(true), 400);
  };

  const removeFromLibrary = async (img_id) => {
    if (!window.confirm('Remover desta galeria?')) return;
    try {
      await api.delete(`/academic/assessments/image-library/${img_id}`);
      setLibItems((prev) => prev.filter((x) => x.id !== img_id));
      toast.success('Removida');
    } catch (e) { toast.error('Falha: ' + (e.response?.data?.detail || e.message)); }
  };

  // V4.3: toggle compartilhamento
  const toggleShareLibrary = async (img_id) => {
    try {
      const { data } = await api.post(`/academic/assessments/image-library/${img_id}/share`);
      setLibItems((prev) => prev.map((x) => x.id === img_id ? { ...x, shared: data.shared } : x));
      toast.success(data.shared ? 'Compartilhada com a equipe' : 'Compartilhamento desativado');
    } catch (e) { toast.error('Falha: ' + (e.response?.data?.detail || e.message)); }
  };

  // V4.3 PREMIUM: toggle favoritar
  const toggleFavoriteLibrary = async (img_id) => {
    try {
      const { data } = await api.post(`/academic/assessments/image-library/${img_id}/favorite`);
      setLibItems((prev) => prev.map((x) => x.id === img_id ? { ...x, favorite: data.favorite } : x));
      toast.success(data.favorite ? 'Adicionada às favoritas' : 'Removida das favoritas');
    } catch (e) { toast.error('Falha: ' + (e.response?.data?.detail || e.message)); }
  };

  // V4.3 PREMIUM: duplicar item da galeria
  const duplicateLibraryItem = async (img_id) => {
    try {
      const { data } = await api.post(`/academic/assessments/image-library/${img_id}/duplicate`);
      if (data?.item) setLibItems((prev) => [{ ...data.item, is_owner: true }, ...prev]);
      toast.success('Imagem duplicada para sua galeria');
    } catch (e) { toast.error('Falha: ' + (e.response?.data?.detail || e.message)); }
  };

  // V4.3 PREMIUM: Variations Viewer — explora a família (raiz + descendentes) de uma imagem
  const [varOpen, setVarOpen] = useState(false);
  const [varBusy, setVarBusy] = useState(false);
  const [varData, setVarData] = useState({ items: [], current_id: null, root_id: null });

  const openVariations = async (img_id) => {
    setVarOpen(true);
    setVarBusy(true);
    setVarData({ items: [], current_id: img_id, root_id: null });
    try {
      const { data } = await api.get(`/academic/assessments/image-library/${img_id}/variations`);
      setVarData({ items: data?.items || [], current_id: data?.current_id, root_id: data?.root_id });
    } catch (e) { toast.error('Falha: ' + (e.response?.data?.detail || e.message)); }
    finally { setVarBusy(false); }
  };

  // V4.3 PREMIUM: regenerar variação (botão na toolbar contextual da imagem selecionada)
  const regenerateSelectedImage = async (style = null) => {
    if (!editor) return;
    const { selection } = editor.state;
    const node = selection?.node;
    if (!node || node.type?.name !== 'image') {
      toast.message('Selecione uma imagem no editor.');
      return;
    }
    const src = node.attrs?.src || '';
    const promptText = node.attrs?.prompt || node.attrs?.alt || '';
    // tenta extrair o ID do asset da URL
    const m = src.match(/\/asset\/([^./?#]+)/);
    const imgId = m ? m[1] : null;
    if (!imgId) { toast.error('Não é possível regenerar esta imagem.'); return; }
    if (!promptText) { toast.error('Imagem sem prompt original — gere uma nova.'); return; }
    toast.message('Regenerando ilustração...');
    try {
      const { data } = await api.post(`/academic/assessments/image-library/${imgId}/regenerate`, {
        style: style || null,
        question_text: promptText,
      });
      const newUrl = data?.url;
      if (!newUrl) { toast.error('Falha ao regenerar.'); return; }
      const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      const fullUrl = newUrl.startsWith('http') ? newUrl : `${base}${newUrl}`;
      editor.chain().focus().updateAttributes('image', { src: fullUrl }).run();
      toast.success('Nova variação gerada');
      setTimeout(() => save(true), 400);
    } catch (e) { toast.error('Falha: ' + (e.response?.data?.detail || e.message)); }
  };

  // ─── Revisão Inteligente (Onda 3) ───
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [reviewData, setReviewData] = useState(null);

  const runReview = async () => {
    if (!editor) return;
    const html = editor.getHTML();
    if (!html || html.length < 30) {
      toast.error('Conteúdo muito curto para revisar.');
      return;
    }
    setReviewBusy(true);
    setReviewOpen(true);
    setReviewData(null);
    try {
      const { data } = await api.post('/academic/assessments/review-ai', {
        current_html: html.slice(0, 60000),
        subject: meta.subject_name || null,
        grade: meta.class_name || null,
      });
      setReviewData(data);
    } catch (e) {
      toast.error('Falha na revisão: ' + (e.response?.data?.detail || e.message));
      setReviewOpen(false);
    } finally {
      setReviewBusy(false);
    }
  };

  // ─── Banco de Questões (Onda 4) ───
  const [bankOpen, setBankOpen] = useState(false);
  const [bankItems, setBankItems] = useState([]);
  const [bankSearch, setBankSearch] = useState('');
  const [bankFavOnly, setBankFavOnly] = useState(false);

  const loadBank = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (bankSearch.trim()) params.set('search', bankSearch.trim());
      if (bankFavOnly) params.set('favorites', 'true');
      const { data } = await api.get(`/academic/assessments/question-bank?${params.toString()}`);
      setBankItems(data || []);
    } catch {
      setBankItems([]);
    }
  }, [bankSearch, bankFavOnly]);

  useEffect(() => {
    if (bankOpen) loadBank();
  }, [bankOpen, loadBank]);

  const saveSelectionToBank = async () => {
    if (!editor) return;
    const { from, to, empty } = editor.state.selection;
    if (empty) {
      toast.message('Selecione a questão antes de salvar no banco.');
      return;
    }
    // Extrai HTML da seleção
    let selectedHtml = '';
    try {
      const slice = editor.state.doc.slice(from, to);
      const div = document.createElement('div');
      const dom = DOMSerializer.fromSchema(editor.state.schema).serializeFragment(slice.content);
      div.appendChild(dom);
      selectedHtml = div.innerHTML;
    } catch {
      selectedHtml = editor.state.doc.textBetween(from, to, '\n');
    }
    if (!selectedHtml.trim()) {
      toast.message('Seleção vazia.');
      return;
    }
    const label = window.prompt('Rótulo para esta questão no banco:', selectedHtml.replace(/<[^>]+>/g, '').slice(0, 60));
    if (!label) return;
    try {
      await api.post('/academic/assessments/question-bank', {
        label: label.trim(),
        html: selectedHtml,
        subject: meta.subject_name || null,
        grade: meta.class_name || null,
        difficulty: 'media',
        qtype: 'objetiva',
      });
      toast.success('Questão salva no banco!');
      if (bankOpen) loadBank();
    } catch (e) {
      toast.error('Falha ao salvar: ' + (e.response?.data?.detail || e.message));
    }
  };

  const insertBankQuestion = async (q) => {
    if (!editor) return;
    editor.commands.focus('end');
    editor.commands.insertContent(q.html);
    try { await api.post(`/academic/assessments/question-bank/${q.id}/use`); } catch { /* noop */ }
    toast.success('Questão inserida do banco');
    setTimeout(() => save(true), 400);
  };

  const toggleBankFavorite = async (q) => {
    try {
      await api.post(`/academic/assessments/question-bank/${q.id}/favorite`);
      loadBank();
    } catch (e) { toast.error('Falha: ' + e.message); }
  };

  const deleteBankQuestion = async (q) => {
    if (!window.confirm(`Excluir "${q.label}" do banco?`)) return;
    try {
      await api.delete(`/academic/assessments/question-bank/${q.id}`);
      loadBank();
      toast.success('Questão removida do banco');
    } catch (e) { toast.error('Falha: ' + e.message); }
  };

  // ─── Bubble: gerar imagem didática para a seleção (V4.3 PREMIUM) ───
  const generateImageForSelection = async () => {
    if (!editor) return;
    // V4.4: usa bloco atual se não houver seleção
    const range = getCurrentRange();
    if (!range) {
      toast.message('Posicione o cursor numa questão.');
      return;
    }
    const { from, to } = range;
    const text = editor.state.doc.textBetween(from, to, ' ');
    if (!text.trim()) { toast.message('Bloco vazio.'); return; }
    setAiSelBusy('img-here');
    try {
      // V4.3 PREMIUM: smart didactic engine (GPT-4o → gpt-image-1, estilo "Livro Didático")
      const { data } = await api.post('/academic/assessments/generate-illustration', {
        question_text: text.slice(0, 1500),
        style: 'didatico',
        size: '1024x1024',
        subject: meta.subject_name || null,
        grade: meta.class_name || null,
      });
      const url = data?.url;
      if (!url) { toast.error('IA não retornou imagem.'); return; }
      const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      const fullUrl = url.startsWith('http') ? url : `${base}${url}`;
      const before = editor.getHTML();
      editor.chain().focus().setTextSelection(to).insertContent({ type: 'image', attrs: { src: fullUrl, alt: text.slice(0, 100), prompt: text.slice(0, 500) } }).run();
      recordAISnapshot('generate', before, editor.getHTML());
      toast.success('Ilustração didática inserida');
      setTimeout(() => save(true), 500);
    } catch (e) {
      toast.error('Falha: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAiSelBusy(null);
    }
  };

  // ─── Auto-Pilot (Feature 1/3) ───
  const [autopilotOpen, setAutopilotOpen] = useState(false);
  const [autopilotBusy, setAutopilotBusy] = useState(false);
  const [autopilotPrompt, setAutopilotPrompt] = useState('');
  const [autopilotStep, setAutopilotStep] = useState('');

  // ─── Menu IA agrupado (toolbar) ───
  const [iaMenuOpen, setIaMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  const runAutopilot = async () => {
    if (!editor) return;
    const prompt = autopilotPrompt.trim();
    if (!prompt) { toast.error('Descreva o que você quer.'); return; }
    setAutopilotBusy(true);
    try {
      setAutopilotStep('Gerando questões + gabarito + competências BNCC...');
      const { data } = await api.post('/academic/assessments/autopilot-ai', {
        prompt,
        subject: meta.subject_name || null,
        grade: meta.class_name || null,
      });
      if (!data?.html) { toast.error('IA não retornou conteúdo.'); return; }
      setAutopilotStep('Aplicando no editor...');
      const before = editor.getHTML();
      editor.commands.setContent(data.html, true);
      recordAISnapshot('generate', before, editor.getHTML());
      await new Promise((r) => setTimeout(r, 800));
      setAutopilotStep('Salvando avaliação...');
      await save(true);
      await new Promise((r) => setTimeout(r, 600));
      setAutopilotStep('Exportando PDFs do aluno e professor...');
      if (id && id !== 'novo') {
        await downloadAs('pdf', 'student');
        await new Promise((r) => setTimeout(r, 600));
        await downloadAs('pdf', 'teacher');
      }
      setAutopilotStep('');
      toast.success('Auto-Pilot concluído! Prova + 2 PDFs prontos.');
      setAutopilotOpen(false);
      setAutopilotPrompt('');
    } catch (e) {
      toast.error('Falha Auto-Pilot: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAutopilotBusy(false);
      setAutopilotStep('');
    }
  };

  // ─── Aplicar Gabarito nas Notas (Feature 2) ───
  const [gradesOpen, setGradesOpen] = useState(false);
  const [gradesBusy, setGradesBusy] = useState(false);
  const [gradesResult, setGradesResult] = useState(null);

  const applyGabaritoToGrades = async (file) => {
    if (!file) return;
    if (!id || id === 'novo') { toast.error('Salve a avaliação primeiro.'); return; }
    setGradesBusy(true);
    setGradesResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post(`/academic/assessments/${id}/apply-gabarito-to-grades`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setGradesResult(data);
      toast.success(`${data.alunos?.length || 0} alunos calculados`);
    } catch (e) {
      toast.error('Falha: ' + (e.response?.data?.detail || e.message));
    } finally {
      setGradesBusy(false);
    }
  };

  const downloadGradesCSV = () => {
    if (!gradesResult?.alunos) return;
    const rows = [['Aluno', 'Acertos', 'Total Questões', 'Nota']];
    gradesResult.alunos.forEach((a) => rows.push([a.aluno, a.acertos, a.total, a.nota.toFixed(2).replace('.', ',')]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(meta.title || 'notas').replace(/[^A-Za-z0-9_-]+/g, '_')}_notas.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };


  const generateModule = async (module) => {
    if (!editor) return;
    const html = editor.getHTML();
    setChatBusy(true);
    try {
      const { data } = await api.post('/academic/assessments/generate-module-ai', {
        module,
        current_html: html.slice(0, 50000),
        subject: meta.subject_name || null,
        grade: meta.class_name || null,
      });
      if (!data?.html) { toast.error('IA não retornou conteúdo.'); return; }
      const before = editor.getHTML();
      editor.commands.focus('end');
      editor.commands.insertContent(data.html);
      recordAISnapshot('generate', before, editor.getHTML());
      const label = { gabarito: 'Gabarito', competencias: 'Competências BNCC', criterios: 'Critérios', cabecalho_extra: 'Instruções' }[module] || module;
      toast.success(`${label} gerado pela IA`);
      setTimeout(() => save(true), 500);
    } catch (e) {
      toast.error('Falha: ' + (e.response?.data?.detail || e.message));
    } finally {
      setChatBusy(false);
    }
  };

  const generateImageAI = async () => {
    if (!editor) return;
    const prompt = imgForm.prompt.trim();
    if (!prompt) {
      toast.error('Descreva o que você quer que a IA desenhe.');
      return;
    }
    setImgBusy(true);
    try {
      // V4.3 PREMIUM: usa endpoint smart didático (GPT-4o interpreta enunciado → gpt-image-1)
      const { data } = await api.post('/academic/assessments/generate-illustration', {
        question_text: prompt,
        size: imgForm.size,
        style: imgForm.style || 'didatico',
        subject: meta.subject_name || null,
        grade: meta.class_name || null,
      });
      const url = data?.url;
      const alt = data?.alt || prompt;
      if (!url) {
        toast.error('A IA não retornou imagem.');
        return;
      }
      const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      const fullUrl = url.startsWith('http') ? url : `${base}${url}`;
      const before = editor.getHTML();
      editor.chain().focus().setImage({ src: fullUrl, alt, prompt }).run();
      recordAISnapshot('generate', before, editor.getHTML());
      toast.success('Imagem didática gerada e inserida!');
      setImgOpen(false);
      setImgForm({ ...imgForm, prompt: '' });
      setTimeout(() => save(true), 500);
    } catch (e) {
      toast.error('Falha ao gerar imagem: ' + (e.response?.data?.detail || e.message));
    } finally {
      setImgBusy(false);
    }
  };

  // ─── Salvar como Modelo Pessoal ───
  const saveAsUserTemplate = async () => {
    if (!id || id === 'novo') {
      toast.message('Salve a avaliação antes de transformá-la em modelo.');
      return;
    }
    const label = window.prompt('Nome para o modelo pessoal:', meta.title || 'Meu Modelo');
    if (!label || !label.trim()) return;
    try {
      await api.post(`/academic/assessments/${id}/save-as-user-template`, {
        label: label.trim(),
        description: `${meta.subject_name || ''} ${meta.class_name ? '· ' + meta.class_name : ''}`.trim(),
      });
      toast.success('Modelo pessoal salvo! Disponível na próxima criação.');
    } catch (e) {
      toast.error('Falha ao salvar modelo: ' + (e.response?.data?.detail || e.message));
    }
  };

  const runAI = async () => {
    if (!editor) return;
    if (!aiForm.prompt.trim()) {
      toast.error('Descreva o que você quer que a IA gere.');
      return;
    }
    setAiBusy(true);
    try {
      const { data } = await api.post('/academic/assessments/generate-ai', {
        prompt: aiForm.prompt,
        type: meta.type,
        subject: meta.subject_name || null,
        grade: meta.class_name || null,
        n_objetivas: Number(aiForm.n_objetivas) || 0,
        n_dissertativas: Number(aiForm.n_dissertativas) || 0,
        include_gabarito: !!aiForm.include_gabarito,
        mode: aiForm.mode,
      });
      const html = data?.html || '';
      if (!html) {
        toast.error('A IA não retornou conteúdo. Tente refinar o prompt.');
        return;
      }
      const beforeSnapshot = editor.getHTML();
      if (aiForm.mode === 'replace') {
        editor.commands.setContent(html, true);
      } else {
        editor.commands.focus('end');
        editor.commands.insertContent(html);
      }
      recordAISnapshot('generate', beforeSnapshot, editor.getHTML());
      toast.success('Conteúdo gerado pela IA');
      setAiOpen(false);
      // Auto-save após geração
      setTimeout(() => save(true), 500);
    } catch (e) {
      toast.error('Falha na geração: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAiBusy(false);
    }
  };

  // ─── AI: Melhorar trecho selecionado (Sticky Bubble Toolbar V4.4) ───
  const [aiSelBusy, setAiSelBusy] = useState(null); // armazena a action em curso

  // V4.4: helper — usa seleção atual; se vazia, expande p/ o bloco/parágrafo do cursor
  const getCurrentRange = () => {
    if (!editor) return null;
    const { from, to, empty } = editor.state.selection;
    if (!empty && from !== to) return { from, to };
    const $pos = editor.state.doc.resolve(from);
    const block = $pos.node($pos.depth);
    if (!block || block.textContent.trim().length === 0) return null;
    const start = $pos.start($pos.depth);
    const end = start + block.nodeSize - 2; // -2 = bordas do node
    return { from: start, to: end };
  };

  const improveSelection = async (action) => {
    if (!editor) return;
    const range = getCurrentRange();
    if (!range) {
      toast.message('Posicione o cursor numa questão ou selecione um trecho.');
      return;
    }
    const { from, to } = range;
    // Extrai HTML do range
    let selectedHtml = '';
    try {
      const { state } = editor;
      const slice = state.doc.slice(from, to);
      const div = document.createElement('div');
      const dom = DOMSerializer.fromSchema(state.schema).serializeFragment(slice.content);
      div.appendChild(dom);
      selectedHtml = div.innerHTML;
    } catch {
      selectedHtml = editor.state.doc.textBetween(from, to, '\n');
    }
    if (!selectedHtml.trim()) {
      toast.message('Bloco vazio.');
      return;
    }
    setAiSelBusy(action);
    try {
      const { data } = await api.post('/academic/assessments/improve-ai', {
        text: selectedHtml,
        action,
        subject: meta.subject_name || null,
        grade: meta.class_name || null,
        target_lang: 'en',
      });
      const newHtml = data?.html || '';
      if (!newHtml) {
        toast.error('A IA não retornou conteúdo.');
        return;
      }
      const beforeSnapshot = editor.getHTML();
      editor.chain().focus().deleteRange({ from, to }).insertContent(newHtml).run();
      recordAISnapshot(action, beforeSnapshot, editor.getHTML());
      toast.success('Trecho atualizado pela IA');
      setTimeout(() => save(true), 500);
    } catch (e) {
      toast.error('Falha IA: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAiSelBusy(null);
    }
  };

  // V4.4: Sticky Bubble Toolbar — handlers de bloco
  const insertNewQuestion = () => {
    if (!editor) return;
    const html = '<p><strong>Questão.</strong> Digite o enunciado aqui.</p>';
    editor.chain().focus().insertContent(html).run();
    toast.success('Nova questão inserida');
    setTimeout(() => save(true), 400);
  };

  const insertComment = () => {
    if (!editor) return;
    const html = '<blockquote style="border-left:4px solid #f59e0b; background:rgba(245,158,11,0.08); padding:6px 12px; color:#92400e; font-style:italic;">💬 Comentário do professor: </blockquote>';
    editor.chain().focus().insertContent(html).run();
    toast.success('Comentário inserido');
    setTimeout(() => save(true), 400);
  };

  const deleteCurrentBlock = () => {
    if (!editor) return;
    const range = getCurrentRange();
    if (!range) { toast.message('Posicione o cursor no bloco.'); return; }
    if (!window.confirm('Excluir esta questão/bloco?')) return;
    const before = editor.getHTML();
    editor.chain().focus().deleteRange({ from: range.from, to: range.to }).run();
    recordAISnapshot('delete-block', before, editor.getHTML());
    toast.success('Bloco removido');
    setTimeout(() => save(true), 400);
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextStyle,
      Color,
      FontFamily,
      Highlight.configure({ multicolor: true }),
      ResizableImage,
      VariableBox,
      ResizableTextBox,
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Table.configure({ resizable: true }),
      TableRow,
      TableHeader,
      TableCell,
      Placeholder.configure({ placeholder: 'Comece a digitar sua avaliação… use a barra superior para formatar.' }),
    ],
    content: '<p></p>',
  });

  // ─── Load existing ───
  useEffect(() => {
    if (!id || id === 'novo') {
      initialLoaded.current = true;
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/academic/assessments/${id}`);
        setMeta({
          title: data.title || 'Avaliação',
          type: data.type || 'trimestral',
          subject_name: data.subject_name || '',
          class_name: data.class_name || '',
          teacher_name: data.teacher_name || '',
          trimester: data.trimester ?? '',
          year: data.year || new Date().getFullYear(),
          valor: data.valor ?? '',
          status: data.status || 'draft',
          // V4.14: refletir estado da fila de impressão ao abrir a avaliação
          print_queue_status: data.print_queue_status || 'rascunho',
          print_queue_id: data.print_queue_id || null,
          data_label: data.data_label || '',
          // V4.17: overrides do cabeçalho
          header_slot_overrides: data.header_slot_overrides || {},
        });
        if (editor && data.content_html) editor.commands.setContent(data.content_html, false);
        initialLoaded.current = true;
      } catch {
        toast.error('Não foi possível carregar a avaliação');
        initialLoaded.current = true;
      }
    })();
  }, [id, editor]);

  // ─── Save ───
  const save = useCallback(async (silent = false) => {
    if (!editor) return;
    const payload = {
      ...meta,
      trimester: meta.trimester ? Number(meta.trimester) : null,
      year: meta.year ? Number(meta.year) : null,
      valor: meta.valor !== '' ? Number(meta.valor) : null,
      content_html: editor.getHTML(),
    };
    setSaving(true);
    try {
      let resp;
      if (!id || id === 'novo') {
        resp = await api.post('/academic/assessments', payload);
        if (resp.data?.id) navigate(`./${resp.data.id}`, { replace: true });
      } else {
        resp = await api.put(`/academic/assessments/${id}`, payload);
      }
      setSavedAt(new Date());
      if (!silent) toast.success('Avaliação salva');
    } catch (e) {
      if (!silent) toast.error('Erro ao salvar: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  }, [editor, meta, id, navigate]);

  // ─── Auto-save (10s) ───
  useEffect(() => {
    if (!editor || !initialLoaded.current) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => save(true), 10000);
    return () => autoSaveTimer.current && clearTimeout(autoSaveTimer.current);
  }, [meta, editor, save]);

  // V4.3 PREMIUM: listener para regenerar imagem (disparado pelo NodeView ResizableImage)
  useEffect(() => {
    const onRegen = (e) => {
      const { src, prompt } = e.detail || {};
      if (!src) return;
      regenerateSelectedImage();
      // se a seleção atual não é a imagem, faz fallback usando o src direto
      if (!editor) return;
      // tenta detectar nó da imagem na seleção; se não, usa src
      const { selection } = editor.state;
      const node = selection?.node;
      if (!node || node.type?.name !== 'image') {
        // fallback: regen via prompt + src
        (async () => {
          const m = (src || '').match(/\/asset\/([^./?#]+)/);
          const imgId = m ? m[1] : null;
          if (!imgId || !prompt) return;
          try {
            const { data } = await api.post(`/academic/assessments/image-library/${imgId}/regenerate`, { question_text: prompt });
            if (data?.url) {
              const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
              const fullUrl = data.url.startsWith('http') ? data.url : `${base}${data.url}`;
              // procura o nó pela src e atualiza
              editor.commands.command(({ tr, state }) => {
                state.doc.descendants((nd, pos) => {
                  if (nd.type.name === 'image' && nd.attrs.src === src) {
                    tr.setNodeMarkup(pos, undefined, { ...nd.attrs, src: fullUrl });
                  }
                });
                return true;
              });
              toast.success('Nova variação gerada');
              setTimeout(() => save(true), 400);
            }
          } catch (er) { toast.error('Falha: ' + (er.response?.data?.detail || er.message)); }
        })();
      }
    };
    window.addEventListener('ae-image-regenerate', onRegen);
    return () => window.removeEventListener('ae-image-regenerate', onRegen);
  }, [editor]);

  // ─── Image upload ───
  const onUploadImage = async () => {
    const inp = document.createElement('input');
    inp.type = 'file';
    inp.accept = 'image/jpeg,image/png,image/webp';
    inp.onchange = async () => {
      const f = inp.files?.[0];
      if (!f) return;
      const fd = new FormData();
      fd.append('file', f);
      try {
        const { data } = await api.post('/academic/assessments/upload-image', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
        const url = `${apiBase}${data.url}`;
        editor.chain().focus().setImage({ src: url }).run();
      } catch (e) {
        toast.error('Falha no upload: ' + (e.response?.data?.detail || e.message));
      }
    };
    inp.click();
  };

  // ─── Exports ───
  const downloadAs = async (fmt, mode = 'teacher') => {
    if (!id || id === 'novo') {
      toast.message('Salve a avaliação antes de exportar.');
      await save();
      return;
    }
    try {
      const resp = await api.post(
        `/academic/assessments/${id}/export/${fmt}?mode=${mode}`,
        {},
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url;
      const suffix = mode === 'student' ? '_aluno' : '_professor';
      a.download = `${(meta.title || 'avaliacao').replace(/[^A-Za-z0-9_-]+/g, '_')}${suffix}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`Falha ao gerar ${fmt.toUpperCase()}: ` + (e.response?.data?.detail || e.message));
    }
  };

  const downloadBothPDFs = async () => {
    if (!id || id === 'novo') {
      toast.message('Salve a avaliação antes de exportar.');
      await save();
      return;
    }
    toast.message('Gerando 2 PDFs (aluno + professor)...');
    await downloadAs('pdf', 'student');
    await new Promise((r) => setTimeout(r, 500));
    await downloadAs('pdf', 'teacher');
    toast.success('2 PDFs baixados!');
  };

  const duplicate = async () => {
    if (!id || id === 'novo') return;
    try {
      const { data } = await api.post(`/academic/assessments/${id}/duplicate`);
      toast.success('Duplicado com sucesso');
      navigate(`../${data.id}`);
    } catch (e) {
      toast.error('Falha ao duplicar');
    }
  };

  if (!editor) return null;

  return (
    <div className="ae-shell" data-testid="assessment-editor">
      {/* SHEET (área central) */}
      <div>
        {/* TOOLBAR */}
        <div className="ae-top">
          <button className="tbtn" onClick={() => navigate(-1)} data-testid="ae-back" title="Voltar"><ArrowLeft size={16} /></button>
          <input
            className="ttitle"
            value={meta.title}
            onChange={(e) => setMeta({ ...meta, title: e.target.value })}
            placeholder="Título da avaliação"
            data-testid="ae-title"
          />

          <div className="group">
            <button className="tbtn" onClick={() => editor.chain().focus().undo().run()} title="Desfazer (Ctrl+Z)"><Undo size={14} /></button>
            <button className="tbtn" onClick={() => editor.chain().focus().redo().run()} title="Refazer (Ctrl+Y)"><Redo size={14} /></button>
          </div>

          <select className="tselect" value="" onChange={(e) => {
            const lv = e.target.value;
            if (lv === 'p') editor.chain().focus().setParagraph().run();
            else if (lv) editor.chain().focus().toggleHeading({ level: Number(lv) }).run();
          }}>
            <option value="">Estilo…</option>
            <option value="p">Parágrafo</option>
            <option value="1">Título 1</option>
            <option value="2">Título 2</option>
            <option value="3">Título 3</option>
          </select>

          <select className="tselect" defaultValue="" onChange={(e) => editor.chain().focus().setFontFamily(e.target.value).run()}>
            <option value="">Fonte…</option>
            <option value="Helvetica, Arial, sans-serif">Sans</option>
            <option value="'Times New Roman', serif">Serif</option>
            <option value="'Courier New', monospace">Mono</option>
          </select>

          <div className="group">
            <button className={`tbtn ${editor.isActive('bold') ? 'active' : ''}`} onClick={() => editor.chain().focus().toggleBold().run()} title="Negrito"><Bold size={14} /></button>
            <button className={`tbtn ${editor.isActive('italic') ? 'active' : ''}`} onClick={() => editor.chain().focus().toggleItalic().run()} title="Itálico"><Italic size={14} /></button>
            <button className={`tbtn ${editor.isActive('underline') ? 'active' : ''}`} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Sublinhado"><UIcon size={14} /></button>
            <button className={`tbtn ${editor.isActive('strike') ? 'active' : ''}`} onClick={() => editor.chain().focus().toggleStrike().run()} title="Tachado"><Strikethrough size={14} /></button>
            <button className={`tbtn ${editor.isActive('highlight') ? 'active' : ''}`} onClick={() => editor.chain().focus().toggleHighlight({ color: '#fef3c7' }).run()} title="Realçar"><Highlighter size={14} /></button>
          </div>

          <div className="group">
            <button className={`tbtn ${editor.isActive({ textAlign: 'left' }) ? 'active' : ''}`} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Esquerda"><AlignLeft size={14} /></button>
            <button className={`tbtn ${editor.isActive({ textAlign: 'center' }) ? 'active' : ''}`} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Centro"><AlignCenter size={14} /></button>
            <button className={`tbtn ${editor.isActive({ textAlign: 'right' }) ? 'active' : ''}`} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Direita"><AlignRight size={14} /></button>
            <button className={`tbtn ${editor.isActive({ textAlign: 'justify' }) ? 'active' : ''}`} onClick={() => editor.chain().focus().setTextAlign('justify').run()} title="Justificar"><AlignJustify size={14} /></button>
          </div>

          <div className="group">
            <button className={`tbtn ${editor.isActive('bulletList') ? 'active' : ''}`} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Lista"><List size={14} /></button>
            <button className={`tbtn ${editor.isActive('orderedList') ? 'active' : ''}`} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numeração"><ListOrdered size={14} /></button>
            <button className="tbtn" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="H1"><Heading1 size={14} /></button>
            <button className="tbtn" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="H2"><Heading2 size={14} /></button>
            <button className="tbtn" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="H3"><Heading3 size={14} /></button>
            <button className={`tbtn ${editor.isActive('blockquote') ? 'active' : ''}`} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Citação"><Quote size={14} /></button>
          </div>

          <div className="group">
            <button className="tbtn" onClick={onUploadImage} title="Inserir Imagem" data-testid="ae-img"><ImgIcon size={14} /></button>
            <button className="tbtn" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Inserir Tabela" data-testid="ae-table"><TableIcon size={14} /></button>
            <button className="tbtn" onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Separador"><Minus size={14} /></button>
            <button className="tbtn" onClick={() => {
              const u = window.prompt('URL do link:');
              if (u) editor.chain().focus().setLink({ href: u }).run();
            }} title="Link"><Link2 size={14} /></button>
            <button className="tbtn" onClick={() => editor.chain().focus().insertContent('<p>Questão X) </p><p style="border-bottom:1px solid #94a3b8;height:16px;"></p>').run()} title="Adicionar questão"><Plus size={14} /></button>
          </div>

          {/* Quebra forçada: linha 2 da toolbar começa aqui (ações + status) */}
          <div className="ae-toolbar-break" aria-hidden="true" />

          <div className="ae-toolbar-actions" data-testid="ae-toolbar-actions">
            {/* Dropdown unificado 🧠 IA */}
            <div className="ae-ia-wrap">
              <button
                className="tbtn ae-ia-trigger"
                onClick={() => setIaMenuOpen((o) => !o)}
                data-testid="ae-ia-menu-open"
                title="Ações de IA"
              >
                <Brain size={14} />
                <span className="ae-ia-trigger-lbl">🧠 IA</span>
                <ChevronDown size={12} />
              </button>
              {iaMenuOpen && (
                <div className="ae-ia-pop" data-testid="ae-ia-menu-pop">
                  <button className="ae-ia-item" onClick={() => { setIaMenuOpen(false); setAutopilotOpen(true); }} data-testid="ae-autopilot-open">
                    <div className="ae-ia-dot" style={{ background: 'linear-gradient(135deg,#ec4899,#A574BF)' }}><Sparkles size={12} /></div>
                    <div><div className="t">Auto-Pilot</div><div className="s">1 frase → prova + PDFs</div></div>
                  </button>
                  <button className="ae-ia-item" onClick={() => { setIaMenuOpen(false); setAiOpen(true); }} data-testid="ae-ai-open">
                    <div className="ae-ia-dot" style={{ background: 'linear-gradient(135deg,#6366f1,#8E52AB)' }}><Sparkles size={12} /></div>
                    <div><div className="t">Gerar prova com IA</div><div className="s">Prompt + configuração</div></div>
                  </button>
                  <button className="ae-ia-item" onClick={() => { setIaMenuOpen(false); runReview(); }} data-testid="ae-review-open">
                    <div className="ae-ia-dot" style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}><ClipboardCheck size={12} /></div>
                    <div><div className="t">Revisar com IA</div><div className="s">BNCC · ortografia · tempo</div></div>
                  </button>
                  <button className="ae-ia-item" onClick={() => { setIaMenuOpen(false); setImgOpen(true); }} data-testid="ae-img-open">
                    <div className="ae-ia-dot" style={{ background: 'linear-gradient(135deg,#f59e0b,#ef4444)' }}><ImagePlus size={12} /></div>
                    <div><div className="t">Gerar imagem</div><div className="s">gpt-image-1</div></div>
                  </button>
                  <button className="ae-ia-item" onClick={() => { setIaMenuOpen(false); setLibOpen(true); setLibScope('mine'); setLibOnlyFav(false); loadLibrary('', 'mine', false); }} data-testid="ae-library-open">
                    <div className="ae-ia-dot" style={{ background: 'linear-gradient(135deg,#22c55e,#16a34a)' }}><Library size={12} /></div>
                    <div><div className="t">Galeria de imagens</div><div className="s">Reutilizar imagens IA</div></div>
                  </button>
                  <button className="ae-ia-item" onClick={() => { setIaMenuOpen(false); setChatOpen(true); }} data-testid="ae-chat-open">
                    <div className="ae-ia-dot" style={{ background: 'linear-gradient(135deg,#0ea5e9,#6366f1)' }}><Brain size={12} /></div>
                    <div><div className="t">Assistente IA</div><div className="s">Chat conversacional</div></div>
                  </button>
                </div>
              )}
            </div>

            {/* Ações: Nova questão · Banco · Notas */}
            <button
              className="tbtn ae-action-pill"
              onClick={() => editor.chain().focus().insertContent('<p><strong>Questão X)</strong> </p>').run()}
              title="Nova questão"
              data-testid="ae-add-question"
            >
              <Plus size={14} /> <span>Nova questão</span>
            </button>
            <button
              className="tbtn ae-action-pill ae-pill-bank"
              onClick={() => setBankOpen((o) => !o)}
              title="Banco de Questões"
              data-testid="ae-bank-open"
            >
              <Library size={14} /> <span>Banco</span>
            </button>
            <button
              className="tbtn ae-action-pill ae-pill-grades"
              onClick={() => setGradesOpen(true)}
              title="Aplicar Gabarito nas Notas"
              data-testid="ae-grades-open"
            >
              <FileCheck2 size={14} /> <span>Notas</span>
            </button>

            {/* Histórico (com badge) */}
            <button
              className="tbtn ae-action-pill ae-pill-hist"
              onClick={() => setAiHistOpen((o) => !o)}
              title={`Histórico IA — ${aiHistory.length}`}
              data-testid="ae-ai-hist-open"
            >
              <History size={14} />
              {aiHistory.length > 0 && <span className="ae-ai-hist-badge">{aiHistory.length}</span>}
            </button>

            {/* Status + ações (direita) */}
            <div className="ae-toolbar-right">
              <span className={`ae-status ${saving ? 'saving' : savedAt ? 'saved' : ''}`} data-testid="ae-status">
                {saving ? 'Salvando…' : savedAt ? `Salvo às ${savedAt.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}` : 'Rascunho'}
              </span>
              {/* V4.14: Badge do status de impressão ao lado do "Rascunho" */}
              {(() => {
                const p = PRINT_STATUS[meta.print_queue_status] || PRINT_STATUS.rascunho;
                return (
                  <span
                    data-testid="ae-print-status-badge"
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
                      background: p.bg, color: p.fg, border: `1px solid ${p.bd}`,
                    }}
                  >{p.label}</span>
                );
              })()}
              <button className="tbtn" onClick={() => save()} title="Salvar (⌘S)" data-testid="ae-save"><Save size={14} /></button>
              {/* V4.14: Botão principal "Enviar para Impressão" — texto explícito */}
              {(meta.print_queue_status === 'enviada' || meta.print_queue_status === 'impressa') ? (
                <span
                  data-testid="ae-sent-to-print-badge"
                  className="tbtn"
                  style={{ background: '#16a34a', color: '#fff', cursor: 'default', pointerEvents: 'none', gap: 4, display: 'inline-flex', alignItems: 'center' }}
                  title={meta.print_queue_status === 'impressa' ? 'Impressa pela secretaria' : 'Enviada para a secretaria imprimir'}
                >
                  <FileCheck2 size={14} />
                  {meta.print_queue_status === 'impressa' ? 'Impressa' : 'Enviada'}
                </span>
              ) : (
                <button
                  className="tbtn"
                  onClick={sendToSecretary}
                  title="Aprova a avaliação e envia para a secretaria imprimir"
                  data-testid="ae-send-secretary"
                  style={{ background: '#4f46e5', color: '#fff', gap: 4, display: 'inline-flex', alignItems: 'center', paddingLeft: 10, paddingRight: 10 }}
                >
                  <Printer size={14} />
                  <span style={{ fontSize: 12, fontWeight: 600 }}>Enviar para Impressão</span>
                </button>
              )}
              <button className="tbtn" onClick={() => window.print()} title="Imprimir" data-testid="ae-print"><Printer size={14} /></button>
              <button className="tbtn" onClick={() => downloadAs('pdf')} title="Exportar PDF" data-testid="ae-pdf"><FileDown size={14} /></button>
              <div className="ae-more-wrap">
                <button className="tbtn" onClick={() => setMoreMenuOpen((o) => !o)} title="Mais ações" data-testid="ae-more-open">
                  <MoreHorizontal size={14} />
                </button>
                {moreMenuOpen && (
                  <div className="ae-ia-pop ae-more-pop" data-testid="ae-more-pop">
                    <button className="ae-ia-item" onClick={() => { setMoreMenuOpen(false); downloadAs('docx'); }}>
                      <div className="ae-ia-dot" style={{ background: '#475569' }}><FileDown size={12} /></div>
                      <div><div className="t">Exportar DOCX</div><div className="s">Arquivo Word editável</div></div>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {aiHistOpen && (
              <div className="ae-ai-hist-pop" data-testid="ae-ai-hist-pop">
                <div className="ae-ai-hist-head">
                  <div>
                    <div className="t"><History size={13} /> Histórico de ações IA</div>
                    <div className="s">Reverta qualquer alteração com 1 clique</div>
                  </div>
                  <button className="ae-ai-close" onClick={() => setAiHistOpen(false)}><X size={14} /></button>
                </div>
                {aiHistory.length === 0 ? (
                  <div className="ae-ai-hist-empty">Nenhuma ação IA registrada nesta avaliação.</div>
                ) : (
                  <div className="ae-ai-hist-list">
                    {aiHistory.map((h) => (
                      <div key={h.id} className="ae-ai-hist-row" data-testid={`ae-ai-hist-row-${h.id}`}>
                        <div className="ae-ai-hist-dot" />
                        <div className="ae-ai-hist-info">
                          <div className="t">{h.label}</div>
                          <div className="s">{new Date(h.ts).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>
                        </div>
                        <button
                          className="ae-ai-hist-revert"
                          onClick={() => revertAI(h.id)}
                          title="Reverter para o estado anterior"
                          data-testid={`ae-ai-revert-${h.id}`}
                        >
                          <RotateCcw size={11} /> Reverter
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* V4.4: STICKY BUBBLE TOOLBAR — sempre visível, atua na questão atual */}
        <div className="ae-sticky-bubble" data-testid="ae-sticky-bubble">
          <div className="ae-sticky-bubble-inner">
            <span className="ae-sticky-bubble-lbl"><Sparkles size={11} /> Questão atual</span>
            <button
              className="ae-sticky-btn"
              onClick={() => setChatOpen(true)}
              data-testid="sb-ia-questao"
              title="Abrir chat IA para esta questão"
            ><Brain size={13} /><span>IA da Questão</span></button>
            <button
              className="ae-sticky-btn"
              onClick={generateImageForSelection}
              disabled={!!aiSelBusy}
              data-testid="sb-img-ia"
              title="Gerar ilustração didática (gpt-image-1)"
            >{aiSelBusy === 'img-here' ? <Loader2 size={13} className="ae-ai-spin" /> : <ImagePlus size={13} />}<span>Imagem IA</span></button>
            <button
              className="ae-sticky-btn"
              onClick={() => {
                if (!editor) return;
                // V4.17: insere caixa de texto flutuante próxima ao topo do corpo
                const nx = 80 + Math.round(Math.random() * 80);
                const ny = 40 + Math.round(Math.random() * 80);
                editor.chain().focus().insertTextBox({ x: nx, y: ny }).run();
                try { toast.success('Caixa de texto inserida — arraste, redimensione e edite.'); } catch (_) {}
              }}
              data-testid="sb-textbox"
              title="Inserir caixa de texto flutuante (arrastar/redimensionar)"
            ><Type size={13} /><span>Caixa de Texto</span></button>
            <button
              className="ae-sticky-btn"
              onClick={insertNewQuestion}
              data-testid="sb-nova-questao"
              title="Inserir nova questão no cursor"
            ><ListPlus size={13} /><span>Nova Questão</span></button>
            <button
              className="ae-sticky-btn"
              onClick={() => setBankOpen(true)}
              data-testid="sb-banco"
              title="Banco de Questões"
            ><Library size={13} /><span>Banco</span></button>
            {/* V4.9: dropdown de variáveis */}
            <div style={{ position: 'relative' }}>
              <button
                className="ae-sticky-btn"
                onClick={() => setVarMenuOpen((v) => !v)}
                data-testid="sb-variavel"
                title="Inserir variável automática"
              ><span style={{ fontFamily: 'monospace', fontWeight: 800 }}>{'{}'}</span><span>Variável</span></button>
              {varMenuOpen && (
                <div
                  data-testid="sb-variavel-menu"
                  style={{ position: 'absolute', top: '110%', left: 0, zIndex: 50, background: '#fff', border: '1px solid rgba(15,23,42,0.10)', borderRadius: 8, boxShadow: '0 6px 20px rgba(15,23,42,0.10)', padding: 4, minWidth: 200 }}
                  onMouseLeave={() => setVarMenuOpen(false)}
                >
                  {VARS_LIST.map((v) => (
                    <button
                      key={v.k}
                      onClick={() => insertVariable(v.k)}
                      data-testid={`sb-var-${v.k}`}
                      style={{ display: 'flex', width: '100%', alignItems: 'center', gap: 8, background: 'transparent', border: 0, padding: '6px 10px', textAlign: 'left', cursor: 'pointer', borderRadius: 4, fontSize: 11.5 }}
                      onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(124,58,237,0.08)'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <code style={{ background: '#f1f5f9', color: '#7B3F98', padding: '1px 5px', borderRadius: 3, fontSize: 10.5 }}>{`{{${v.k}}}`}</code>
                      <span>{v.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              className="ae-sticky-btn"
              onClick={openMailMerge}
              data-testid="sb-mala-direta"
              title="Mala-Direta: 1 PDF por aluno da turma"
            ><span style={{ fontSize: 12 }}>📬</span><span>Mala-Direta</span></button>
            <span className="ae-sticky-sep" />
            <button
              className="ae-sticky-btn"
              onClick={() => improveSelection('rewrite')}
              disabled={!!aiSelBusy}
              data-testid="sb-reescrever"
              title="Reescrever esta questão"
            >{aiSelBusy === 'rewrite' ? <Loader2 size={13} className="ae-ai-spin" /> : <RotateCcw size={13} />}<span>Reescrever</span></button>
            <button
              className="ae-sticky-btn"
              onClick={() => improveSelection('summarize')}
              disabled={!!aiSelBusy}
              data-testid="sb-simplificar"
              title="Simplificar linguagem"
            >{aiSelBusy === 'summarize' ? <Loader2 size={13} className="ae-ai-spin" /> : <Minimize2 size={13} />}<span>Simplificar</span></button>
            <button
              className="ae-sticky-btn"
              onClick={() => improveSelection('easier')}
              disabled={!!aiSelBusy}
              data-testid="sb-mais-facil"
              title="Tornar mais fácil"
            >{aiSelBusy === 'easier' ? <Loader2 size={13} className="ae-ai-spin" /> : <ChevronDown size={13} />}<span>+ Fácil</span></button>
            <button
              className="ae-sticky-btn"
              onClick={() => improveSelection('harder')}
              disabled={!!aiSelBusy}
              data-testid="sb-mais-dificil"
              title="Tornar mais difícil"
            >{aiSelBusy === 'harder' ? <Loader2 size={13} className="ae-ai-spin" /> : <ChevronUp size={13} />}<span>+ Difícil</span></button>
            <button
              className="ae-sticky-btn"
              onClick={() => improveSelection('explain-answer')}
              disabled={!!aiSelBusy}
              data-testid="sb-gabarito"
              title="Gerar gabarito comentado para esta questão"
            >{aiSelBusy === 'explain-answer' ? <Loader2 size={13} className="ae-ai-spin" /> : <ClipboardCheck size={13} />}<span>Gabarito</span></button>
            <button
              className="ae-sticky-btn"
              onClick={insertComment}
              data-testid="sb-comentario"
              title="Inserir comentário do professor"
            ><MessageSquare size={13} /><span>Comentário</span></button>
            <span className="ae-sticky-sep" />
            <button
              className="ae-sticky-btn ae-sticky-btn-danger"
              onClick={deleteCurrentBlock}
              data-testid="sb-excluir"
              title="Excluir esta questão"
            ><Trash2 size={13} /><span>Excluir</span></button>
          </div>
        </div>

        {/* PÁGINA A4 */}
        <div className="ae-page-wrap">
          <div className="ae-sheet" data-testid="ae-sheet">
            {/* V4.5: CABEÇALHO 100% EDITÁVEL — agora com background PNG (#cabeçalho 2026) + V4.6 template picker */}
            <div
              className={`ae-hdr-editable ae-print-keep tpl-${(meta.header_template || 'dna_2026').startsWith('custom_') ? 'custom' : (meta.header_template || 'dna_2026')}`}
              data-testid="ae-hdr-editable"
              data-ae-header="1"
              data-template={meta.header_template || 'dna_2026'}
              style={getCurrentTemplateBg() ? { backgroundImage: `url(${getCurrentTemplateBg()})` } : undefined}
            >
              {/* V4.6: picker flutuante + V4.7 botão "Gerenciar" (apenas no editor, escondido no print) */}
              <div className="ae-hdr-tpl-picker no-print" data-testid="ae-hdr-tpl-picker">
                <span className="ae-hdr-tpl-lbl">Template:</span>
                <select
                  value={meta.header_template || 'dna_2026'}
                  onChange={(e) => setMeta({ ...meta, header_template: e.target.value })}
                  data-testid="ae-hdr-tpl-select"
                >
                  {tplList.length === 0 ? (
                    <>
                      <option value="dna_2026">DNA 2026 (Padrão)</option>
                      <option value="minimal">Minimalista</option>
                    </>
                  ) : tplList.map((t) => (
                    <option key={t.key} value={t.key}>
                      {t.label}{t.is_custom ? ' ★' : ''}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => { setTplMgrOpen(true); loadTemplatesList(); }}
                  data-testid="ae-hdr-tpl-manage"
                  title="Gerenciar templates"
                  style={{ background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 4, padding: '2px 6px', cursor: 'pointer', fontSize: 10, color: '#7B3F98', fontWeight: 700 }}
                >+ Gerenciar</button>
              </div>
              <div className="ae-hdr-form">
                <div className="ae-hdr-row" data-slot-row="nome">
                  <div className="ae-hdr-field f-grow" data-slot="nome">
                    <span className="ae-hdr-lbl">Nome</span>
                    <span
                      className="ae-hdr-val"
                      contentEditable
                      suppressContentEditableWarning
                      data-testid="ae-hdr-aluno"
                      data-placeholder="{{aluno_nome}}"
                      onBlur={(e) => setMeta({ ...meta, aluno_nome: e.currentTarget.textContent.trim() })}
                    >{meta.aluno_nome || ''}</span>
                  </div>
                </div>
                <div className="ae-hdr-row" data-slot-row="row2">
                  <div className="ae-hdr-field f-sm" data-slot="numero">
                    <span className="ae-hdr-lbl">Nº</span>
                    <span className="ae-hdr-val" contentEditable suppressContentEditableWarning data-testid="ae-hdr-numero" data-placeholder="{{numero_chamada}}" onBlur={(e) => setMeta({ ...meta, numero_chamada: e.currentTarget.textContent.trim() })}>{meta.numero_chamada || ''}</span>
                  </div>
                  <div className="ae-hdr-field f-md" data-slot="turma">
                    <span className="ae-hdr-lbl">Ano</span>
                    <span className="ae-hdr-val" contentEditable suppressContentEditableWarning data-testid="ae-hdr-ano" data-placeholder="{{turma}}" onBlur={(e) => setMeta({ ...meta, class_name: e.currentTarget.textContent.trim() })}>{meta.class_name || ''}</span>
                  </div>
                  <div className="ae-hdr-field f-grow" data-slot="disciplina">
                    <span className="ae-hdr-lbl">Disciplina</span>
                    <span className="ae-hdr-val" contentEditable suppressContentEditableWarning data-testid="ae-hdr-disciplina" data-placeholder="{{disciplina}}" onBlur={(e) => setMeta({ ...meta, subject_name: e.currentTarget.textContent.trim() })}>{meta.subject_name || ''}</span>
                  </div>
                </div>
                <div className="ae-hdr-row" data-slot-row="professor">
                  <div className="ae-hdr-field f-grow" data-slot="professor">
                    <span className="ae-hdr-lbl">Professor(a)</span>
                    <span className="ae-hdr-val" contentEditable suppressContentEditableWarning data-testid="ae-hdr-professor" data-placeholder="{{professor}}" onBlur={(e) => setMeta({ ...meta, teacher_name: e.currentTarget.textContent.trim() })}>{meta.teacher_name || ''}</span>
                  </div>
                </div>
                <div className="ae-hdr-band-area" data-slot="tituloFaixa">
                  <span className="ae-hdr-band-val" contentEditable suppressContentEditableWarning data-testid="ae-hdr-tipo" data-placeholder="{{tipo_avaliacao}} {{trimestre}}" onBlur={(e) => { const v = e.currentTarget.textContent.trim(); setMeta({ ...meta, type_label: v }); }}>{meta.type_label || TYPE_LABELS[meta.type] || 'AVALIAÇÃO'}</span>
                </div>
                <div className="ae-hdr-row" data-slot-row="row4">
                  <div className="ae-hdr-field f-md" data-slot="valor">
                    <span className="ae-hdr-lbl">Valor</span>
                    <span className="ae-hdr-val" contentEditable suppressContentEditableWarning data-testid="ae-hdr-valor" data-placeholder="{{valor}}" onBlur={(e) => { const t = e.currentTarget.textContent.trim().replace(',', '.'); const n = parseFloat(t); setMeta({ ...meta, valor: isNaN(n) ? meta.valor : n }); }}>{(meta.valor !== '' && meta.valor !== null && meta.valor !== undefined) ? Number(meta.valor).toFixed(2).replace('.', ',') : ''}</span>
                  </div>
                  <div className="ae-hdr-field f-md" data-slot="data">
                    <span className="ae-hdr-lbl">Data</span>
                    <span className="ae-hdr-val" contentEditable suppressContentEditableWarning data-testid="ae-hdr-data" data-placeholder="{{data}}" onBlur={(e) => setMeta({ ...meta, data_label: e.currentTarget.textContent.trim() })}>{meta.data_label || ''}</span>
                  </div>
                  <div className="ae-hdr-field f-md ae-hdr-field-nota" data-slot="nota">
                    <span className="ae-hdr-lbl">Nota</span>
                    <span className="ae-hdr-val" contentEditable suppressContentEditableWarning data-testid="ae-hdr-nota" data-placeholder="{{nota}}">{''}</span>
                  </div>
                </div>
              </div>
            </div>
            <EditorContent editor={editor} />
          </div>
        </div>
      </div>

      {/* SIDEBAR */}
      <aside className="ae-side ae-print-hide" data-testid="ae-side">
        <h3>Dados da avaliação</h3>
        <label>Tipo</label>        <select value={meta.type} onChange={(e) => setMeta({ ...meta, type: e.target.value })} data-testid="ae-type">
          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <label>Status</label>
        <select value={meta.status} onChange={(e) => setMeta({ ...meta, status: e.target.value })}>
          {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>

        <h3>Contexto</h3>
        <label>Disciplina</label>
        <input value={meta.subject_name} onChange={(e) => setMeta({ ...meta, subject_name: e.target.value })} placeholder="Ex.: Matemática" />
        <label>Turma</label>
        <input value={meta.class_name} onChange={(e) => setMeta({ ...meta, class_name: e.target.value })} placeholder="Ex.: 6º ANO A" />
        <label>Professor(a)</label>
        <input value={meta.teacher_name} onChange={(e) => setMeta({ ...meta, teacher_name: e.target.value })} placeholder="Nome do professor" />

        <div className="row2">
          <div>
            <label>Trimestre</label>
            <select value={meta.trimester} onChange={(e) => setMeta({ ...meta, trimester: e.target.value })}>
              <option value="">—</option><option value="1">1º</option><option value="2">2º</option><option value="3">3º</option>
            </select>
          </div>
          <div>
            <label>Ano</label>
            <input type="number" value={meta.year} onChange={(e) => setMeta({ ...meta, year: e.target.value })} />
          </div>
        </div>
        <label>Valor (nota máxima)</label>
        <input type="number" step="0.5" value={meta.valor} onChange={(e) => setMeta({ ...meta, valor: e.target.value })} placeholder="Ex.: 10" />

        <h3>Ações</h3>
        <button className="btn" onClick={() => save()} data-testid="ae-save-side"><Save size={14} /> Salvar</button>
        <button className="btn ghost" onClick={() => downloadAs('pdf', 'student')} data-testid="ae-pdf-aluno">
          <FileDown size={14} /> PDF do Aluno
          <span className="ae-btn-hint">sem gabarito</span>
        </button>
        <button className="btn ghost" onClick={() => downloadAs('pdf', 'teacher')} data-testid="ae-pdf-professor">
          <FileDown size={14} /> PDF do Professor
          <span className="ae-btn-hint">com gabarito</span>
        </button>
        <button className="btn primary-soft" onClick={downloadBothPDFs} data-testid="ae-pdf-both">
          <FileDown size={14} /> Baixar os 2 PDFs
          <span className="ae-btn-hint">aluno + professor</span>
        </button>
        <button className="btn ghost" onClick={() => downloadAs('docx')}><FileDown size={14} /> Exportar DOCX</button>
        <button className="btn ghost" onClick={() => window.print()}><Printer size={14} /> Imprimir</button>
        {id && id !== 'novo' && (
          <button className="btn ghost" onClick={duplicate}><CopyIcon size={14} /> Duplicar</button>
        )}
        {id && id !== 'novo' && (
          <button className="btn ghost" onClick={saveAsUserTemplate} data-testid="ae-save-as-tpl">
            <BookmarkPlus size={14} /> Salvar como Modelo
          </button>
        )}

        {/* Engajamento IA: contador semanal */}
        <div className="ae-ai-weekly" data-testid="ae-ai-weekly">
          <div className="ae-ai-weekly-ico"><Sparkles size={14} /></div>
          <div className="ae-ai-weekly-text">
            <div className="n">{aiWeekly}</div>
            <div className="l">{aiWeekly === 1 ? 'melhoria IA' : 'melhorias IA'} esta semana</div>
          </div>
        </div>
      </aside>

      {/* PAINEL LATERAL: Assistente IA (Onda 1) */}
      {chatOpen && (
        <div className="ae-chat-panel" data-testid="ae-chat-panel">
          <div className="ae-chat-head">
            <div className="ae-chat-title">
              <div className="ae-chat-ico"><Brain size={16} /></div>
              <div>
                <div className="t">Assistente IA</div>
                <div className="s">DNACore · gpt-4o</div>
              </div>
            </div>
            <button className="ae-chat-close" onClick={() => setChatOpen(false)} data-testid="ae-chat-close"><X size={16} /></button>
          </div>

          <div className="ae-chat-quick">
            {/* Geração modular (Onda 5) */}
            <button className="ae-chat-chip ae-chip-mod" onClick={() => generateModule('gabarito')} disabled={chatBusy} title="Gerar gabarito comentado">
              ✚ Gabarito
            </button>
            <button className="ae-chat-chip ae-chip-mod" onClick={() => generateModule('competencias')} disabled={chatBusy} title="Mapear competências BNCC">
              ✚ Competências BNCC
            </button>
            <button className="ae-chat-chip ae-chip-mod" onClick={() => generateModule('criterios')} disabled={chatBusy} title="Critérios de correção">
              ✚ Critérios
            </button>
            <button className="ae-chat-chip ae-chip-mod" onClick={() => generateModule('cabecalho_extra')} disabled={chatBusy} title="Instruções para aluno">
              ✚ Instruções
            </button>
            {CHAT_QUICK_ACTIONS.map((q, i) => (
              <button
                key={i}
                className="ae-chat-chip"
                onClick={() => sendChat(q)}
                disabled={chatBusy}
                title={q}
              >
                {q.length > 36 ? q.slice(0, 33) + '...' : q}
              </button>
            ))}
          </div>

          <div className="ae-chat-body" data-testid="ae-chat-body">
            {chatMessages.map((m, idx) => (
              <div key={idx} className={`ae-chat-msg ${m.role}`}>
                {m.role === 'assistant' && (
                  <div className="ae-chat-avatar"><Brain size={12} /></div>
                )}
                <div className="ae-chat-bubble">{m.content}</div>
              </div>
            ))}
            {chatBusy && (
              <div className="ae-chat-msg assistant">
                <div className="ae-chat-avatar"><Brain size={12} /></div>
                <div className="ae-chat-bubble ae-chat-typing">
                  <Loader2 size={12} className="ae-ai-spin" /> Pensando...
                </div>
              </div>
            )}
            <div ref={chatBottomRef} />
          </div>

          <div className="ae-chat-foot">
            <textarea
              className="ae-chat-input"
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendChat();
                }
              }}
              placeholder='Ex.: "Crie 10 questões sobre frações para o 6º ano" — Enter envia, Shift+Enter quebra linha'
              rows={2}
              disabled={chatBusy}
              data-testid="ae-chat-input"
            />
            <button
              className="ae-chat-send"
              onClick={() => sendChat()}
              disabled={chatBusy || !chatInput.trim()}
              data-testid="ae-chat-send"
            >
              {chatBusy ? <Loader2 size={14} className="ae-ai-spin" /> : <Send size={14} />}
            </button>
          </div>
        </div>
      )}

      {/* MODAL: Auto-Pilot (Feature 1/3) */}
      {autopilotOpen && (
        <div className="ae-ai-overlay" onClick={() => !autopilotBusy && setAutopilotOpen(false)} data-testid="ae-autopilot-modal">
          <div className="ae-ai-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ae-ai-head">
              <div className="ae-ai-title">
                <div className="ae-ai-spark" style={{ background: 'linear-gradient(135deg,#ec4899,#A574BF)' }}>
                  <Sparkles size={16} />
                </div>
                <div>
                  <div className="t">Auto-Pilot</div>
                  <div className="s">1 frase → prova + gabarito + competências + 2 PDFs</div>
                </div>
              </div>
              <button className="ae-ai-close" onClick={() => !autopilotBusy && setAutopilotOpen(false)} disabled={autopilotBusy}><X size={16} /></button>
            </div>
            <div className="ae-ai-body">
              <label className="ae-ai-lbl">Descreva a prova ideal em 1 frase</label>
              <textarea
                className="ae-ai-textarea"
                rows={4}
                value={autopilotPrompt}
                onChange={(e) => setAutopilotPrompt(e.target.value)}
                placeholder={'Ex.: "Prova de Mat 7º ano sobre frações equivalentes, 12 questões (10 objetivas + 2 dissertativas), alinhada à BNCC, com gabarito comentado".'}
                data-testid="ae-autopilot-prompt"
                disabled={autopilotBusy}
              />
              <div className="ae-ai-hint">
                A IA criará a prova, gabarito, competências BNCC, salvará e exportará os 2 PDFs (aluno + professor) automaticamente.
              </div>
              {autopilotBusy && autopilotStep && (
                <div className="ae-autopilot-step">
                  <Loader2 size={14} className="ae-ai-spin" /> {autopilotStep}
                </div>
              )}
            </div>
            <div className="ae-ai-foot">
              <button className="ae-ai-cancel" onClick={() => setAutopilotOpen(false)} disabled={autopilotBusy}>Cancelar</button>
              <button
                className="ae-ai-go"
                style={{ background: 'linear-gradient(135deg,#ec4899,#A574BF)' }}
                onClick={runAutopilot}
                disabled={autopilotBusy}
                data-testid="ae-autopilot-go"
              >
                {autopilotBusy ? <><Loader2 size={14} className="ae-ai-spin" /> Em execução...</> : <><Sparkles size={14} /> Decolar 🚀</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Aplicar Gabarito → Notas (Feature 2) */}
      {gradesOpen && (
        <div className="ae-ai-overlay" onClick={() => !gradesBusy && setGradesOpen(false)} data-testid="ae-grades-modal">
          <div className="ae-ai-modal" style={{ maxWidth: 620 }} onClick={(e) => e.stopPropagation()}>
            <div className="ae-ai-head">
              <div className="ae-ai-title">
                <div className="ae-ai-spark" style={{ background: 'linear-gradient(135deg,#14b8a6,#0d9488)' }}>
                  <FileCheck2 size={16} />
                </div>
                <div>
                  <div className="t">Aplicar Gabarito nas Notas</div>
                  <div className="s">CSV/XLSX de respostas → notas calculadas automaticamente</div>
                </div>
              </div>
              <button className="ae-ai-close" onClick={() => setGradesOpen(false)}><X size={16} /></button>
            </div>
            <div className="ae-ai-body">
              <label className="ae-ai-lbl">Planilha de respostas dos alunos</label>
              <div className="ae-grades-upload">
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={(e) => applyGabaritoToGrades(e.target.files?.[0])}
                  disabled={gradesBusy}
                  data-testid="ae-grades-file"
                />
                <div className="ae-grades-hint">
                  Formato esperado: 1ª coluna = Nome do aluno, demais colunas = Q1, Q2, Q3... com A/B/C/D/E.
                  <br />A IA usa o gabarito desta avaliação para calcular a nota proporcional ao valor ({meta.valor || '—'}).
                </div>
              </div>

              {gradesBusy && (
                <div className="ae-autopilot-step"><Loader2 size={14} className="ae-ai-spin" /> Calculando notas...</div>
              )}

              {gradesResult?.alunos && (
                <div className="ae-grades-result" data-testid="ae-grades-result">
                  <div className="ae-grades-summary">
                    <div><strong>{gradesResult.alunos.length}</strong> alunos calculados</div>
                    <div>Média turma: <strong>{gradesResult.media?.toFixed(2).replace('.', ',') || '—'}</strong></div>
                  </div>
                  <div className="ae-grades-table-wrap">
                    <table className="ae-grades-table">
                      <thead><tr><th>Aluno</th><th>Acertos</th><th>Nota</th></tr></thead>
                      <tbody>
                        {gradesResult.alunos.slice(0, 30).map((a, i) => (
                          <tr key={i}>
                            <td>{a.aluno}</td>
                            <td>{a.acertos}/{a.total}</td>
                            <td><strong>{a.nota.toFixed(2).replace('.', ',')}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {gradesResult.alunos.length > 30 && (
                    <div className="ae-grades-hint">+ {gradesResult.alunos.length - 30} alunos no CSV de download</div>
                  )}
                </div>
              )}
            </div>
            <div className="ae-ai-foot">
              <button className="ae-ai-cancel" onClick={() => setGradesOpen(false)}>Fechar</button>
              {gradesResult?.alunos && (
                <button className="ae-ai-go" style={{ background: 'linear-gradient(135deg,#14b8a6,#0d9488)' }} onClick={downloadGradesCSV} data-testid="ae-grades-download">
                  <FileDown size={14} /> Baixar planilha de notas (CSV)
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PAINEL: Revisão Inteligente (Onda 3) */}
      {reviewOpen && (
        <div className="ae-review-overlay" onClick={() => setReviewOpen(false)} data-testid="ae-review-panel">
          <div className="ae-review-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ae-review-head">
              <div className="ae-review-title">
                <div className="ae-review-ico"><ClipboardCheck size={16} /></div>
                <div>
                  <div className="t">Revisão Inteligente</div>
                  <div className="s">BNCC · ortografia · tempo · ambiguidade</div>
                </div>
              </div>
              <button className="ae-ai-close" onClick={() => setReviewOpen(false)}><X size={16} /></button>
            </div>
            <div className="ae-review-body">
              {reviewBusy && (
                <div className="ae-review-loading">
                  <Loader2 size={20} className="ae-ai-spin" />
                  <div>Analisando a prova com IA...</div>
                </div>
              )}
              {!reviewBusy && reviewData && (
                <>
                  <div className="ae-review-summary">
                    <div className="ae-review-nota">{reviewData.nota_geral ?? '—'}</div>
                    <div>
                      <div className="ae-review-nota-lbl">Nota geral · IA</div>
                      <div className="ae-review-tempo">⏱ Tempo estimado: {reviewData.tempo_estimado_min ?? '—'} min</div>
                    </div>
                  </div>
                  <p className="ae-review-resumo">{reviewData.resumo_geral || '—'}</p>

                  {reviewData.bncc && (
                    <div className="ae-review-section">
                      <div className="ae-review-section-t">📚 BNCC</div>
                      <div className="ae-review-bncc-row">
                        <span className={`ae-review-pill ${reviewData.bncc.alinhamento || ''}`}>
                          {reviewData.bncc.alinhamento || '—'}
                        </span>
                        <span style={{ fontSize: 11.5, color: '#475569' }}>{reviewData.bncc.observacao || ''}</span>
                      </div>
                      {(reviewData.bncc.competencias_sugeridas || []).length > 0 && (
                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 6 }}>
                          {reviewData.bncc.competencias_sugeridas.map((c, i) => (
                            <span key={i} className="ae-review-code">{c}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {reviewData.equilibrio_dificuldade && (
                    <div className="ae-review-section">
                      <div className="ae-review-section-t">⚖️ Equilíbrio de dificuldade</div>
                      <div style={{ display: 'flex', gap: 12, fontSize: 12 }}>
                        <span>🟢 Fácil: <strong>{reviewData.equilibrio_dificuldade.facil ?? 0}</strong></span>
                        <span>🟡 Médio: <strong>{reviewData.equilibrio_dificuldade.medio ?? 0}</strong></span>
                        <span>🔴 Difícil: <strong>{reviewData.equilibrio_dificuldade.dificil ?? 0}</strong></span>
                      </div>
                      <div style={{ fontSize: 11.5, color: '#475569', marginTop: 4 }}>{reviewData.equilibrio_dificuldade.comentario || ''}</div>
                    </div>
                  )}

                  {['ortografia', 'gramatica', 'clareza', 'ambiguidade', 'repeticoes'].map((dim) => {
                    const list = reviewData[dim] || [];
                    if (!Array.isArray(list) || list.length === 0) return null;
                    const labels = { ortografia: '✏️ Ortografia', gramatica: '📐 Gramática', clareza: '🔍 Clareza', ambiguidade: '❓ Ambiguidade', repeticoes: '🔁 Repetições' };
                    return (
                      <div key={dim} className="ae-review-section">
                        <div className="ae-review-section-t">{labels[dim]} <span className="ae-review-count">{list.length}</span></div>
                        {list.slice(0, 6).map((it, i) => (
                          <div key={i} className="ae-review-issue">
                            <div className="ae-review-issue-q">{it.trecho || it.questao || it.problema || JSON.stringify(it).slice(0, 80)}</div>
                            {it.sugestao && <div className="ae-review-issue-s">→ {it.sugestao}</div>}
                            {Array.isArray(it.interpretacoes) && <div className="ae-review-issue-s">→ {it.interpretacoes.join(' / ')}</div>}
                          </div>
                        ))}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PAINEL: Banco de Questões (Onda 4) */}
      {bankOpen && (
        <div className="ae-bank-panel" data-testid="ae-bank-panel">
          <div className="ae-bank-head">
            <div className="ae-chat-title">
              <div className="ae-chat-ico" style={{ background: 'linear-gradient(135deg,#10b981,#059669)' }}><Library size={16} /></div>
              <div>
                <div className="t">Banco de Questões</div>
                <div className="s">Reutilize · favorite · arraste para a prova</div>
              </div>
            </div>
            <button className="ae-chat-close" onClick={() => setBankOpen(false)}><X size={16} /></button>
          </div>
          <div className="ae-bank-filter">
            <div className="ae-bank-search">
              <Search size={12} />
              <input
                value={bankSearch}
                onChange={(e) => setBankSearch(e.target.value)}
                placeholder="Buscar..."
                data-testid="ae-bank-search"
              />
            </div>
            <button
              className={`ae-bank-favbtn ${bankFavOnly ? 'on' : ''}`}
              onClick={() => setBankFavOnly((v) => !v)}
              data-testid="ae-bank-favtoggle"
            >
              <Star size={12} fill={bankFavOnly ? '#f59e0b' : 'none'} /> Favoritas
            </button>
          </div>
          <div className="ae-bank-list">
            {bankItems.length === 0 ? (
              <div className="ae-bank-empty">
                Nenhuma questão salva ainda.<br />
                <span style={{ fontSize: 11 }}>Selecione uma questão no editor → &quot;Salvar Banco&quot; no bubble menu.</span>
              </div>
            ) : (
              bankItems.map((q) => (
                <div key={q.id} className="ae-bank-card" data-testid={`ae-bank-card-${q.id}`}>
                  <div className="ae-bank-card-head">
                    <button className="ae-bank-fav" onClick={() => toggleBankFavorite(q)} title="Favoritar">
                      <Star size={13} fill={q.favorite ? '#f59e0b' : 'none'} color={q.favorite ? '#f59e0b' : '#94a3b8'} />
                    </button>
                    <div className="ae-bank-label">{q.label}</div>
                    <button className="ae-bank-del" onClick={() => deleteBankQuestion(q)} title="Excluir">
                      <Trash2 size={11} />
                    </button>
                  </div>
                  <div className="ae-bank-meta">
                    {q.subject && <span>{q.subject}</span>}
                    {q.grade && <span>{q.grade}</span>}
                    {q.difficulty && <span className={`ae-bank-diff ${q.difficulty}`}>{q.difficulty}</span>}
                  </div>
                  <button className="ae-bank-insert" onClick={() => insertBankQuestion(q)} data-testid={`ae-bank-insert-${q.id}`}>
                    Inserir na prova
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* MODAL: Gerar Imagem com IA (Onda 2) */}
      {imgOpen && (
        <div className="ae-ai-overlay" onClick={() => !imgBusy && setImgOpen(false)} data-testid="ae-img-modal">
          <div className="ae-ai-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ae-ai-head">
              <div className="ae-ai-title">
                <div className="ae-ai-spark" style={{ background: 'linear-gradient(135deg,#f59e0b,#ef4444)' }}>
                  <ImagePlus size={16} />
                </div>
                <div>
                  <div className="t">Gerar Imagem com IA</div>
                  <div className="s">gpt-image-1 · ilustrações pedagógicas</div>
                </div>
              </div>
              <button className="ae-ai-close" onClick={() => !imgBusy && setImgOpen(false)} disabled={imgBusy}><X size={16} /></button>
            </div>

            <div className="ae-ai-body">
              <label className="ae-ai-lbl">Descreva a imagem que você quer</label>
              <textarea
                className="ae-ai-textarea"
                rows={4}
                value={imgForm.prompt}
                onChange={(e) => setImgForm({ ...imgForm, prompt: e.target.value })}
                placeholder={'Ex.: "Mapa do Brasil sem legendas com estados destacados" · "Célula vegetal com organelas e legendas" · "Gráfico de barras com população por região"'}
                data-testid="ae-img-prompt"
                disabled={imgBusy}
              />
              <div className="ae-ai-hint">
                Contexto enviado: <strong>{meta.subject_name || 'Sem disciplina'}</strong> · <strong>{meta.class_name || 'Sem turma'}</strong>
              </div>

              <div style={{ marginTop: 14 }}>
                <label className="ae-ai-lbl">Formato</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {[
                    { v: '1024x1024', label: 'Quadrada', emoji: '◼' },
                    { v: '1536x1024', label: 'Paisagem', emoji: '▭' },
                    { v: '1024x1536', label: 'Retrato',  emoji: '▯' },
                  ].map((s) => (
                    <button
                      key={s.v}
                      type="button"
                      onClick={() => setImgForm({ ...imgForm, size: s.v })}
                      disabled={imgBusy}
                      data-testid={`ae-img-size-${s.v}`}
                      style={{
                        flex: 1,
                        padding: '10px 8px',
                        border: imgForm.size === s.v ? '2px solid #f59e0b' : '1px solid rgba(15,23,42,0.10)',
                        background: imgForm.size === s.v ? 'rgba(245,158,11,0.08)' : '#fff',
                        borderRadius: 10,
                        cursor: 'pointer',
                        fontSize: 11.5,
                        fontWeight: 700,
                        color: '#0f172a',
                        transition: 'all 140ms ease',
                      }}
                    >
                      <div style={{ fontSize: 18, marginBottom: 2 }}>{s.emoji}</div>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* V4.3 PREMIUM: Style picker (10 estilos didáticos) */}
              <div style={{ marginTop: 14 }}>
                <label className="ae-ai-lbl">Estilo da ilustração</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6 }} data-testid="ae-img-style-grid">
                  {ILLUSTRATION_STYLES.map((st) => (
                    <button
                      key={st.key}
                      type="button"
                      onClick={() => setImgForm({ ...imgForm, style: st.key })}
                      disabled={imgBusy}
                      data-testid={`ae-img-style-${st.key}`}
                      style={{
                        padding: '8px 4px',
                        border: imgForm.style === st.key ? '2px solid #7B3F98' : '1px solid rgba(15,23,42,0.10)',
                        background: imgForm.style === st.key ? 'rgba(124,58,237,0.08)' : '#fff',
                        borderRadius: 8,
                        cursor: 'pointer',
                        fontSize: 10.5,
                        fontWeight: 600,
                        color: '#0f172a',
                        transition: 'all 140ms ease',
                      }}
                    >
                      <div style={{ fontSize: 16, marginBottom: 1 }}>{st.emoji}</div>
                      {st.label}
                    </button>
                  ))}
                </div>
                <div className="ae-ai-hint" style={{ marginTop: 8 }}>
                  A IA interpretará o enunciado e produzirá uma ilustração que representa exatamente o conteúdo. <strong>O enunciado vira o prompt automaticamente.</strong>
                </div>
              </div>
            </div>

            <div className="ae-ai-foot">
              <button className="ae-ai-cancel" onClick={() => setImgOpen(false)} disabled={imgBusy}>Cancelar</button>
              <button
                className="ae-ai-go"
                style={{ background: 'linear-gradient(135deg,#f59e0b,#ef4444)' }}
                onClick={generateImageAI}
                disabled={imgBusy}
                data-testid="ae-img-go"
              >
                {imgBusy ? <><Loader2 size={14} className="ae-ai-spin" /> Gerando... (até 60s)</> : <><ImagePlus size={14} /> Gerar imagem</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Galeria de Imagens IA (V4.2 / V4.3 com tabs) */}
      {libOpen && (
        <div className="ae-ai-overlay" onClick={() => setLibOpen(false)} data-testid="ae-library-modal">
          <div className="ae-ai-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 820 }}>
            <div className="ae-ai-head">
              <div className="ae-ai-title">
                <div className="ae-ai-spark" style={{ background: 'linear-gradient(135deg,#22c55e,#16a34a)' }}><Library size={16} /></div>
                <div>
                  <div className="t">Galeria de Imagens IA</div>
                  <div className="s">Reutilize imagens · compartilhe com sua equipe</div>
                </div>
              </div>
              <button className="ae-ai-close" onClick={() => setLibOpen(false)}><X size={16} /></button>
            </div>
            <div className="ae-ai-body">
              {/* V4.3: Scope tabs */}
              <div style={{ display: 'flex', gap: 4, marginBottom: 10, borderBottom: '1px solid #e2e8f0' }} data-testid="ae-library-tabs">
                {[
                  { key: 'mine', label: 'Minhas' },
                  { key: 'shared', label: 'Compartilhadas' },
                  { key: 'all', label: 'Todas' },
                ].map((t) => (
                  <button
                    key={t.key}
                    onClick={() => { setLibScope(t.key); loadLibrary(libQ, t.key, libOnlyFav); }}
                    data-testid={`ae-library-tab-${t.key}`}
                    style={{
                      padding: '6px 14px',
                      fontSize: 12,
                      fontWeight: 600,
                      border: 0,
                      background: 'transparent',
                      color: libScope === t.key ? '#7B3F98' : '#64748b',
                      borderBottom: libScope === t.key ? '2px solid #7B3F98' : '2px solid transparent',
                      cursor: 'pointer',
                      marginBottom: -1,
                    }}
                  >{t.label}</button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center' }}>
                <input
                  type="text"
                  placeholder="Buscar por prompt..."
                  value={libQ}
                  onChange={(e) => setLibQ(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadLibrary(libQ, libScope, libOnlyFav)}
                  style={{ flex: 1, padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 8 }}
                  data-testid="ae-library-search"
                />
                <button className="tbtn" onClick={() => loadLibrary(libQ, libScope, libOnlyFav)} title="Buscar"><Search size={14} /></button>
                <button
                  onClick={() => { const nf = !libOnlyFav; setLibOnlyFav(nf); loadLibrary(libQ, libScope, nf); }}
                  data-testid="ae-library-fav-filter"
                  title={libOnlyFav ? 'Mostrar todas' : 'Somente favoritas'}
                  style={{
                    padding: '7px 12px',
                    border: libOnlyFav ? '2px solid #f59e0b' : '1px solid #cbd5e1',
                    background: libOnlyFav ? 'rgba(245,158,11,0.10)' : '#fff',
                    color: libOnlyFav ? '#b45309' : '#475569',
                    borderRadius: 8,
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >★ Favoritas</button>
              </div>
              {libBusy ? (
                <div style={{ textAlign: 'center', padding: 24, color: '#64748b' }}><Loader2 className="ae-ai-spin" /> Carregando...</div>
              ) : libItems.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 24, color: '#64748b' }}>
                  {libScope === 'shared' ? 'Nenhuma imagem compartilhada pela equipe ainda.' : libScope === 'all' ? 'Nenhuma imagem disponível.' : 'Nenhuma imagem na galeria ainda. Gere uma pelo botão "Gerar imagem".'}
                </div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, maxHeight: 420, overflowY: 'auto' }}>
                  {libItems.map((it) => {
                    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
                    const src = it.url?.startsWith('http') ? it.url : `${base}${it.url}`;
                    return (
                      <div key={it.id} style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden', background: '#fff', position: 'relative' }} data-testid={`ae-library-item-${it.id}`}>
                        <img src={src} alt={it.prompt || ''} style={{ width: '100%', height: 130, objectFit: 'cover', cursor: 'pointer' }} onClick={() => insertFromLibrary(it)} />
                        {it.shared && (
                          <span style={{ position: 'absolute', top: 6, left: 6, background: '#16a34a', color: '#fff', fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>Compartilhada</span>
                        )}
                        {it.favorite && (
                          <span style={{ position: 'absolute', top: 6, left: it.shared ? 92 : 6, background: '#f59e0b', color: '#fff', fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>★</span>
                        )}
                        {it.style && (
                          <span style={{ position: 'absolute', bottom: 52, left: 6, background: 'rgba(124,58,237,0.92)', color: '#fff', fontSize: 9, padding: '2px 5px', borderRadius: 4, fontWeight: 600 }}>{(ILLUSTRATION_STYLES.find((s) => s.key === it.style) || {}).label || it.style}</span>
                        )}
                        {!it.is_owner && it.user_name && (
                          <span style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(15,23,42,0.85)', color: '#fff', fontSize: 9, padding: '2px 5px', borderRadius: 4 }}>por {it.user_name}</span>
                        )}
                        <div style={{ padding: 6, fontSize: 11, color: '#475569', height: 40, overflow: 'hidden' }}>{(it.prompt || '').slice(0, 80)}</div>
                        <div style={{ display: 'flex', gap: 3, padding: 6, flexWrap: 'wrap' }}>
                          <button className="ae-ai-go" style={{ flex: '1 1 auto', minWidth: 60, padding: '4px 6px', fontSize: 11, background: 'linear-gradient(135deg,#6366f1,#8E52AB)' }} onClick={() => insertFromLibrary(it)} data-testid={`ae-library-insert-${it.id}`}>Inserir</button>
                          {it.is_owner ? (
                            <>
                              <button
                                className="ae-ai-cancel"
                                style={{ padding: '4px 6px', fontSize: 11, background: it.favorite ? '#f59e0b' : '#fff', color: it.favorite ? '#fff' : '#475569' }}
                                onClick={() => toggleFavoriteLibrary(it.id)}
                                title={it.favorite ? 'Desfavoritar' : 'Favoritar'}
                                data-testid={`ae-library-favorite-${it.id}`}
                              >★</button>
                              <button
                                className="ae-ai-cancel"
                                style={{ padding: '4px 6px', fontSize: 11, background: it.shared ? '#16a34a' : '#fff', color: it.shared ? '#fff' : '#475569' }}
                                onClick={() => toggleShareLibrary(it.id)}
                                title={it.shared ? 'Descompartilhar' : 'Compartilhar com a equipe'}
                                data-testid={`ae-library-share-${it.id}`}
                              >↗</button>
                              <button
                                className="ae-ai-cancel"
                                style={{ padding: '4px 6px', fontSize: 11 }}
                                onClick={() => openVariations(it.id)}
                                title="Ver variações desta imagem"
                                data-testid={`ae-library-variations-${it.id}`}
                              >⚡</button>
                              <button className="ae-ai-cancel" style={{ padding: '4px 6px', fontSize: 11 }} onClick={() => removeFromLibrary(it.id)} title="Remover" data-testid={`ae-library-delete-${it.id}`}><Trash2 size={12} /></button>
                            </>
                          ) : (
                            <>
                              <button
                                className="ae-ai-cancel"
                                style={{ padding: '4px 6px', fontSize: 11 }}
                                onClick={() => duplicateLibraryItem(it.id)}
                                title="Duplicar para minha galeria"
                                data-testid={`ae-library-duplicate-${it.id}`}
                              ><CopyIcon size={12} /></button>
                              <button
                                className="ae-ai-cancel"
                                style={{ padding: '4px 6px', fontSize: 11 }}
                                onClick={() => openVariations(it.id)}
                                title="Ver variações"
                                data-testid={`ae-library-variations-${it.id}`}
                              >⚡</button>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="ae-ai-foot">
              <button className="ae-ai-cancel" onClick={() => setLibOpen(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Variations Viewer (V4.3 PREMIUM) — árvore de variações */}
      {varOpen && (
        <div className="ae-ai-overlay" onClick={() => setVarOpen(false)} data-testid="ae-variations-modal">
          <div className="ae-ai-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 900 }}>
            <div className="ae-ai-head">
              <div className="ae-ai-title">
                <div className="ae-ai-spark" style={{ background: 'linear-gradient(135deg,#A574BF,#ec4899)' }}>⚡</div>
                <div>
                  <div className="t">Variações desta imagem</div>
                  <div className="s">{varData.items.length} variação(ões) na família · raiz + descendentes</div>
                </div>
              </div>
              <button className="ae-ai-close" onClick={() => setVarOpen(false)}><X size={16} /></button>
            </div>
            <div className="ae-ai-body">
              {varBusy ? (
                <div style={{ textAlign: 'center', padding: 24, color: '#64748b' }}><Loader2 className="ae-ai-spin" /> Carregando família...</div>
              ) : varData.items.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 24, color: '#64748b' }}>Nenhuma variação encontrada.</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, maxHeight: 480, overflowY: 'auto' }} data-testid="ae-variations-grid">
                  {varData.items.map((it, idx) => {
                    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
                    const src = it.url?.startsWith('http') ? it.url : `${base}${it.url}`;
                    const isCurrent = it.id === varData.current_id;
                    return (
                      <div
                        key={it.id}
                        data-testid={`ae-variations-item-${it.id}`}
                        style={{
                          border: isCurrent ? '2px solid #A574BF' : it.is_root ? '2px solid #16a34a' : '1px solid #e2e8f0',
                          borderRadius: 10,
                          overflow: 'hidden',
                          background: '#fff',
                          position: 'relative',
                          boxShadow: isCurrent ? '0 0 0 3px rgba(168,85,247,0.15)' : 'none',
                        }}
                      >
                        <div style={{ position: 'absolute', top: 6, left: 6, display: 'flex', gap: 4, zIndex: 2 }}>
                          <span style={{ background: 'rgba(15,23,42,0.85)', color: '#fff', fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700 }}>#{idx + 1}</span>
                          {it.is_root && <span style={{ background: '#16a34a', color: '#fff', fontSize: 9, padding: '2px 5px', borderRadius: 4, fontWeight: 700 }}>RAIZ</span>}
                          {isCurrent && <span style={{ background: '#A574BF', color: '#fff', fontSize: 9, padding: '2px 5px', borderRadius: 4, fontWeight: 700 }}>ATUAL</span>}
                          {it.favorite && <span style={{ background: '#f59e0b', color: '#fff', fontSize: 9, padding: '2px 5px', borderRadius: 4, fontWeight: 700 }}>★</span>}
                        </div>
                        <img src={src} alt="" style={{ width: '100%', height: 150, objectFit: 'cover', display: 'block', cursor: 'pointer' }} onClick={() => { insertFromLibrary(it); setVarOpen(false); }} />
                        <div style={{ padding: 8, fontSize: 11, color: '#475569' }}>
                          <div style={{ fontWeight: 600, color: '#0f172a' }}>{(ILLUSTRATION_STYLES.find((s) => s.key === it.style) || {}).label || it.style || '—'}</div>
                          <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2 }}>
                            {it.created_at ? new Date(it.created_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : ''}
                            {it.user_name ? ` · ${it.user_name}` : ''}
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 4, padding: 8, paddingTop: 0 }}>
                          <button
                            className="ae-ai-go"
                            style={{ flex: 1, padding: '5px 6px', fontSize: 11, background: 'linear-gradient(135deg,#A574BF,#ec4899)' }}
                            onClick={() => { insertFromLibrary(it); setVarOpen(false); }}
                            data-testid={`ae-variations-insert-${it.id}`}
                          >Inserir esta</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {varData.items.length > 0 && (
                <div style={{ marginTop: 12, padding: 10, background: '#f8fafc', borderRadius: 8, fontSize: 11, color: '#64748b' }}>
                  💡 Borda <span style={{ color: '#16a34a', fontWeight: 700 }}>verde</span> = raiz da árvore · borda <span style={{ color: '#A574BF', fontWeight: 700 }}>roxa</span> = variação que você clicou para ver
                </div>
              )}
            </div>
            <div className="ae-ai-foot">
              <button className="ae-ai-cancel" onClick={() => setVarOpen(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* V4.7: MODAL Custom Templates Manager */}
      {tplMgrOpen && (
        <div className="ae-ai-overlay" onClick={() => setTplMgrOpen(false)} data-testid="ae-tpl-mgr-modal">
          <div className="ae-ai-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 720 }}>
            <div className="ae-ai-head">
              <div className="ae-ai-title">
                <div className="ae-ai-spark" style={{ background: 'linear-gradient(135deg,#0ea5e9,#7B3F98)' }}>🎨</div>
                <div>
                  <div className="t">Templates de Cabeçalho</div>
                  <div className="s">Upload PNG personalizado + gestão</div>
                </div>
              </div>
              <button className="ae-ai-close" onClick={() => setTplMgrOpen(false)}><X size={16} /></button>
            </div>
            <div className="ae-ai-body">
              <div style={{ padding: 12, background: '#f8fafc', borderRadius: 8, marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>+ Novo Template (Upload PNG)</div>
                <input
                  type="text"
                  placeholder="Nome (ex: Avaliação Trimestral 2026)"
                  value={tplForm.label}
                  onChange={(e) => setTplForm({ ...tplForm, label: e.target.value })}
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, marginBottom: 6, fontSize: 12 }}
                  data-testid="ae-tpl-label"
                />
                <input
                  type="text"
                  placeholder="Descrição (opcional)"
                  value={tplForm.description}
                  onChange={(e) => setTplForm({ ...tplForm, description: e.target.value })}
                  style={{ width: '100%', padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: 6, marginBottom: 6, fontSize: 12 }}
                  data-testid="ae-tpl-desc"
                />
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={(e) => setTplForm({ ...tplForm, file: e.target.files?.[0] || null })}
                  style={{ width: '100%', padding: '4px 0', fontSize: 11, marginBottom: 8 }}
                  data-testid="ae-tpl-file"
                />
                <div style={{ fontSize: 10, color: '#64748b', marginBottom: 8 }}>
                  💡 Dica: o PNG ideal é horizontal (~2.47:1), 5MB máx. As posições dos campos usarão as percentuais padrão do template DNA 2026 (Nome top:13%, Nº top:35%, faixa central top:75%, etc.).
                </div>
                <button
                  onClick={uploadCustomTemplate}
                  disabled={tplBusy || !tplForm.file || !tplForm.label.trim()}
                  className="ae-ai-go"
                  style={{ background: 'linear-gradient(135deg,#0ea5e9,#7B3F98)', width: '100%', padding: '8px 12px' }}
                  data-testid="ae-tpl-upload"
                >{tplBusy ? <><Loader2 size={14} className="ae-ai-spin" /> Enviando...</> : 'Criar Template'}</button>
              </div>

              <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 8 }}>Templates disponíveis ({tplList.length})</div>
              <div style={{ maxHeight: 320, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {tplList.map((t) => (
                  <div key={t.key} data-testid={`ae-tpl-item-${t.key}`} style={{ display: 'flex', gap: 10, padding: 8, border: '1px solid #e2e8f0', borderRadius: 6, alignItems: 'center', background: '#fff' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{t.label}{t.is_custom && <span style={{ marginLeft: 6, color: '#7B3F98', fontSize: 10 }}>★ CUSTOM</span>}</div>
                      <div style={{ fontSize: 10, color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.description || ''}</div>
                      {t.user_name && <div style={{ fontSize: 9, color: '#94a3b8' }}>por {t.user_name}</div>}
                    </div>
                    <button
                      onClick={() => { setMeta({ ...meta, header_template: t.key }); toast.success(`Aplicado: ${t.label}`); }}
                      style={{ padding: '4px 10px', fontSize: 11, fontWeight: 600, background: meta.header_template === t.key ? '#7B3F98' : '#fff', color: meta.header_template === t.key ? '#fff' : '#7B3F98', border: '1px solid #7B3F98', borderRadius: 4, cursor: 'pointer' }}
                      data-testid={`ae-tpl-apply-${t.key}`}
                    >{meta.header_template === t.key ? 'Em uso' : 'Aplicar'}</button>
                    {t.is_custom && (
                      <>
                        <button
                          onClick={() => openPosEditor(t)}
                          style={{ padding: '4px 8px', fontSize: 11, background: '#fff', color: '#0ea5e9', border: '1px solid #7dd3fc', borderRadius: 4, cursor: 'pointer' }}
                          data-testid={`ae-tpl-positions-${t.key}`}
                          title="Editar posições dos campos sobre o PNG"
                        >📐</button>
                        <button
                          onClick={() => deleteCustomTemplate(t.key)}
                          style={{ padding: '4px 8px', fontSize: 11, background: 'transparent', color: '#ef4444', border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer' }}
                          data-testid={`ae-tpl-delete-${t.key}`}
                          title="Remover"
                        ><Trash2 size={12} /></button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="ae-ai-foot">
              <button className="ae-ai-cancel" onClick={() => setTplMgrOpen(false)}>Fechar</button>
            </div>
          </div>
        </div>
      )}

      {/* V4.8: MODAL Visual Positions Editor */}
      {posEditorOpen && posEditorTpl && (
        <div className="ae-ai-overlay" data-testid="ae-pos-editor-modal">
          <div className="ae-ai-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 1100 }}>
            <div className="ae-ai-head">
              <div className="ae-ai-title">
                <div className="ae-ai-spark" style={{ background: 'linear-gradient(135deg,#0ea5e9,#06b6d4)' }}>📐</div>
                <div>
                  <div className="t">Editor Visual de Posições</div>
                  <div className="s">{posEditorTpl.label} · arraste as caixas sobre o PNG</div>
                </div>
              </div>
              <button className="ae-ai-close" onClick={() => setPosEditorOpen(false)}><X size={16} /></button>
            </div>
            <div className="ae-ai-body" style={{ padding: 0 }}>
              <div style={{ padding: '10px 14px', fontSize: 11, color: '#475569', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                💡 Arraste cada caixinha colorida para a área correspondente do PNG. Para redimensionar, arraste a borda direita. As posições são salvas em % da imagem (responsivo).
              </div>
              <div
                ref={posCanvasRef}
                data-testid="ae-pos-canvas"
                style={{
                  position: 'relative',
                  width: '100%',
                  aspectRatio: '2.47 / 1',
                  backgroundImage: `url(${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/academic/assessments/header-templates/${posEditorTpl.key}/bg.png)`,
                  backgroundSize: '100% 100%',
                  backgroundRepeat: 'no-repeat',
                  userSelect: 'none',
                  cursor: 'crosshair',
                }}
              >
                {FIELD_KEYS.map((f) => {
                  const p = posData[f.key] || DEFAULT_POS[f.key];
                  return (
                    <div
                      key={f.key}
                      data-testid={`ae-pos-box-${f.key}`}
                      style={{
                        position: 'absolute',
                        left: `${p.left}%`,
                        top: `${p.top}%`,
                        width: `${p.width}%`,
                        height: '14%',
                        background: `${f.color}33`,
                        border: `2px solid ${f.color}`,
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '0 6px',
                        cursor: 'move',
                        fontSize: 10,
                        fontWeight: 700,
                        color: f.color,
                        textShadow: '0 0 3px rgba(255,255,255,0.9)',
                      }}
                      onMouseDown={(e) => startPosDrag(e, f.key, 'drag')}
                    >
                      <span>{f.label}</span>
                      <span
                        onMouseDown={(e) => startPosDrag(e, f.key, 'resize')}
                        style={{ width: 12, height: 12, background: f.color, cursor: 'ew-resize', borderRadius: 2, marginRight: -3 }}
                        title="Arraste para redimensionar largura"
                      />
                    </div>
                  );
                })}
              </div>
              <div style={{ padding: '8px 14px', fontSize: 10, color: '#64748b', display: 'flex', flexWrap: 'wrap', gap: 8, borderTop: '1px solid #e2e8f0' }}>
                {FIELD_KEYS.map((f) => (
                  <span key={f.key} style={{ color: f.color, fontWeight: 600 }}>
                    {f.label}: {Math.round((posData[f.key] || DEFAULT_POS[f.key]).left)}%,{Math.round((posData[f.key] || DEFAULT_POS[f.key]).top)}% · w{Math.round((posData[f.key] || DEFAULT_POS[f.key]).width)}%
                  </span>
                ))}
              </div>
            </div>
            <div className="ae-ai-foot">
              <button className="ae-ai-cancel" onClick={() => { setPosData({ ...DEFAULT_POS }); }} data-testid="ae-pos-reset">↺ Resetar p/ Padrão</button>
              <button className="ae-ai-cancel" onClick={() => setPosEditorOpen(false)}>Cancelar</button>
              <button className="ae-ai-go" onClick={savePositions} style={{ background: 'linear-gradient(135deg,#0ea5e9,#06b6d4)' }} data-testid="ae-pos-save">Salvar Posições</button>
            </div>
          </div>
        </div>
      )}

      {/* V4.10: MODAL Mala-Direta */}
      {mailMergeOpen && (
        <div className="ae-ai-overlay" onClick={() => !mailMergeBusy && setMailMergeOpen(false)} data-testid="ae-mailmerge-modal">
          <div className="ae-ai-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="ae-ai-head">
              <div className="ae-ai-title">
                <div className="ae-ai-spark" style={{ background: 'linear-gradient(135deg,#f59e0b,#ef4444)' }}>📬</div>
                <div>
                  <div className="t">Mala-Direta</div>
                  <div className="s">Gerar 1 PDF por aluno da turma · ZIP único</div>
                </div>
              </div>
              <button className="ae-ai-close" onClick={() => setMailMergeOpen(false)} disabled={mailMergeBusy}><X size={16} /></button>
            </div>
            <div className="ae-ai-body">
              <label className="ae-ai-lbl">Turma</label>
              <select
                value={mailMergeForm.class_group_id}
                onChange={(e) => setMailMergeForm({ ...mailMergeForm, class_group_id: e.target.value })}
                data-testid="mm-class-select"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, marginBottom: 12 }}
              >
                <option value="">Selecione uma turma...</option>
                {mailMergeClasses.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}{c.year ? ` · ${c.year}` : ''}</option>
                ))}
              </select>

              <label className="ae-ai-lbl">Modo</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                <button
                  type="button"
                  onClick={() => setMailMergeForm({ ...mailMergeForm, mode: 'student' })}
                  data-testid="mm-mode-student"
                  style={{ flex: 1, padding: '8px 12px', border: mailMergeForm.mode === 'student' ? '2px solid #7B3F98' : '1px solid #cbd5e1', background: mailMergeForm.mode === 'student' ? 'rgba(124,58,237,0.08)' : '#fff', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >🎓 Aluno (sem gabarito)</button>
                <button
                  type="button"
                  onClick={() => setMailMergeForm({ ...mailMergeForm, mode: 'teacher' })}
                  data-testid="mm-mode-teacher"
                  style={{ flex: 1, padding: '8px 12px', border: mailMergeForm.mode === 'teacher' ? '2px solid #7B3F98' : '1px solid #cbd5e1', background: mailMergeForm.mode === 'teacher' ? 'rgba(124,58,237,0.08)' : '#fff', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >👨‍🏫 Professor (com gabarito)</button>
              </div>

              <div style={{ padding: 10, background: '#fef3c7', borderRadius: 6, fontSize: 11, color: '#92400e' }}>
                💡 Cada PDF terá <code>{'{{aluno_nome}}'}</code> e <code>{'{{numero_chamada}}'}</code> preenchidos para o aluno específico. As caixas continuam posicionadas onde você as colocou.
              </div>
            </div>
            <div className="ae-ai-foot">
              <button className="ae-ai-cancel" onClick={() => setMailMergeOpen(false)} disabled={mailMergeBusy}>Cancelar</button>
              <button
                className="ae-ai-go"
                onClick={runMailMerge}
                disabled={mailMergeBusy || !mailMergeForm.class_group_id}
                data-testid="mm-go"
                style={{ background: 'linear-gradient(135deg,#f59e0b,#ef4444)' }}
              >{mailMergeBusy ? <><Loader2 size={14} className="ae-ai-spin" /> Gerando ZIP...</> : <>📬 Gerar Mala-Direta</>}</button>
            </div>
          </div>
        </div>
      )}

      {/* V4.11: MODAL Enviar para Secretaria */}
      {secretaryOpen && (
        <div className="ae-ai-overlay" onClick={() => !secretaryBusy && setSecretaryOpen(false)} data-testid="ae-secretary-modal">
          <div className="ae-ai-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540 }}>
            <div className="ae-ai-head">
              <div className="ae-ai-title">
                <div className="ae-ai-spark" style={{ background: 'linear-gradient(135deg,#0ea5e9,#16a34a)' }}>📬</div>
                <div>
                  <div className="t">Enviar para Secretaria</div>
                  <div className="s">A avaliação irá para a fila de impressão</div>
                </div>
              </div>
              <button className="ae-ai-close" onClick={() => setSecretaryOpen(false)} disabled={secretaryBusy}><X size={16} /></button>
            </div>
            <div className="ae-ai-body">
              <label className="ae-ai-lbl">Cópias extras</label>
              <input
                type="number"
                min="0"
                value={secretaryForm.copias_extras}
                onChange={(e) => setSecretaryForm({ ...secretaryForm, copias_extras: e.target.value })}
                data-testid="sec-copias-extras"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, marginBottom: 10 }}
              />
              <label className="ae-ai-lbl">Data da prova</label>
              <input
                type="text"
                placeholder="dd/mm/aaaa"
                value={secretaryForm.data_prova}
                onChange={(e) => setSecretaryForm({ ...secretaryForm, data_prova: e.target.value })}
                data-testid="sec-data"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, marginBottom: 10 }}
              />
              <label className="ae-ai-lbl">Plano (opcional)</label>
              <input
                type="text"
                placeholder="ex: Manhã, Integral, etc."
                value={secretaryForm.plano}
                onChange={(e) => setSecretaryForm({ ...secretaryForm, plano: e.target.value })}
                data-testid="sec-plano"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12, marginBottom: 10 }}
              />
              <div style={{ padding: 10, background: '#dbeafe', borderRadius: 6, fontSize: 11, color: '#1e40af' }}>
                💡 Total de cópias = nº de alunos da turma + cópias extras. A secretaria receberá um registro no Relatório de Impressão.
              </div>
            </div>
            <div className="ae-ai-foot">
              <button className="ae-ai-cancel" onClick={() => setSecretaryOpen(false)} disabled={secretaryBusy}>Cancelar</button>
              <button
                className="ae-ai-go"
                onClick={confirmSendToSecretary}
                disabled={secretaryBusy}
                data-testid="sec-confirm"
                style={{ background: 'linear-gradient(135deg,#0ea5e9,#16a34a)' }}
              >{secretaryBusy ? <><Loader2 size={14} className="ae-ai-spin" /> Enviando...</> : <>📬 Enviar para Secretaria</>}</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL: Gerar com IA */}
      {aiOpen && (        <div className="ae-ai-overlay" onClick={() => !aiBusy && setAiOpen(false)} data-testid="ae-ai-modal">
          <div className="ae-ai-modal" onClick={(e) => e.stopPropagation()}>
            <div className="ae-ai-head">
              <div className="ae-ai-title">
                <div className="ae-ai-spark"><Sparkles size={16} /></div>
                <div>
                  <div className="t">Gerar avaliação com IA</div>
                  <div className="s">ChatGPT cria as questões a partir da sua instrução</div>
                </div>
              </div>
              <button className="ae-ai-close" onClick={() => !aiBusy && setAiOpen(false)} disabled={aiBusy}><X size={16} /></button>
            </div>

            <div className="ae-ai-body">
              <label className="ae-ai-lbl">Descreva o que você quer que a IA gere</label>
              <textarea
                className="ae-ai-textarea"
                rows={4}
                value={aiForm.prompt}
                onChange={(e) => setAiForm({ ...aiForm, prompt: e.target.value })}
                placeholder={'Ex.: 10 questões sobre Equações do 1º grau · 8º ano · contexto cotidiano com 2 problemas envolvendo dinheiro e idade.'}
                data-testid="ae-ai-prompt"
                disabled={aiBusy}
              />
              <div className="ae-ai-hint">
                Disciplina e Turma da barra lateral já são enviados como contexto. <strong>{meta.subject_name || 'Sem disciplina'}</strong> · <strong>{meta.class_name || 'Sem turma'}</strong>
              </div>

              <div className="ae-ai-grid">
                <div>
                  <label className="ae-ai-lbl">Questões objetivas</label>
                  <input
                    type="number" min="0" max="30"
                    value={aiForm.n_objetivas}
                    onChange={(e) => setAiForm({ ...aiForm, n_objetivas: e.target.value })}
                    data-testid="ae-ai-n-obj"
                    disabled={aiBusy}
                  />
                </div>
                <div>
                  <label className="ae-ai-lbl">Questões dissertativas</label>
                  <input
                    type="number" min="0" max="10"
                    value={aiForm.n_dissertativas}
                    onChange={(e) => setAiForm({ ...aiForm, n_dissertativas: e.target.value })}
                    data-testid="ae-ai-n-dis"
                    disabled={aiBusy}
                  />
                </div>
              </div>

              <div className="ae-ai-row">
                <label className="ae-ai-check">
                  <input
                    type="checkbox"
                    checked={aiForm.include_gabarito}
                    onChange={(e) => setAiForm({ ...aiForm, include_gabarito: e.target.checked })}
                    data-testid="ae-ai-gabarito"
                    disabled={aiBusy}
                  />
                  Incluir gabarito comentado
                </label>
                <div className="ae-ai-modeg">
                  <label className={aiForm.mode === 'append' ? 'on' : ''}>
                    <input type="radio" name="aimode" checked={aiForm.mode === 'append'}
                      onChange={() => setAiForm({ ...aiForm, mode: 'append' })} disabled={aiBusy} />
                    Acrescentar
                  </label>
                  <label className={aiForm.mode === 'replace' ? 'on' : ''}>
                    <input type="radio" name="aimode" checked={aiForm.mode === 'replace'}
                      onChange={() => setAiForm({ ...aiForm, mode: 'replace' })} disabled={aiBusy} />
                    Substituir tudo
                  </label>
                </div>
              </div>
            </div>

            <div className="ae-ai-foot">
              <button className="ae-ai-cancel" onClick={() => setAiOpen(false)} disabled={aiBusy}>Cancelar</button>
              <button className="ae-ai-go" onClick={runAI} disabled={aiBusy} data-testid="ae-ai-go">
                {aiBusy ? <><Loader2 size={14} className="ae-ai-spin" /> Gerando…</> : <><Sparkles size={14} /> Gerar agora</>}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
